import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  MapPin, Plus, Trash2, Check, X, Clock, Route, Loader2,
  LocateFixed, Edit2, RotateCcw, Map, RefreshCw, Flag, Navigation,
  FileText, Share2, Copy, Link2, ArrowLeftRight, Play, BarChart3, Archive, MoreVertical, Wrench, Star, Sparkles
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '../../lib/api';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

// Import extracted components
import {
  createNumberedIcon,
  createStartEndIcon,
  FitBounds,
  formatTime,
  SortableStopItem
} from './Routes';

// Import new dialogs
import RouteAnalyticsDialog from './RouteAnalyticsDialog';
import AddStopMidRouteDialog from './AddStopMidRouteDialog';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Build the printable HTML for a Route PDF. Used by handleDownloadRoutePdf.
function buildRoutePrintHtml(data) {
  const route = data.route || {};
  const server = data.server || {};
  const directions = data.directions || null;
  const stops = route.stops || [];
  const esc = (s) => String(s == null ? '' : s).replace(/[<>&]/g, (c) => ({'<':'&lt;','>':'&gt;','&':'&amp;'}[c]));
  const fmtDate = (d) => {
    if (!d) return '';
    try { return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }); } catch { return d; }
  };
  const fmtMi = (m) => (m == null ? '' : `${(m / 1609.34).toFixed(1)} mi`);
  const fmtDur = (s) => {
    if (s == null) return '';
    const mins = Math.round(s / 60);
    return mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins} min`;
  };

  // Resolve the start & end locations so the PDF clearly shows where the
  // driver begins and finishes — not just the stops in between.
  const startAddr = route.start_address || (route.use_current_location ? 'Current location' : null);
  const endAddr = route.return_to_start
    ? (route.start_address || (route.use_current_location ? 'Current location' : null))
    : route.end_address;

  const startRow = startAddr ? `
    <tr class="endpoint-row">
      <td class="stop-n endpoint-start">S</td>
      <td>
        <div class="stop-addr">${esc(startAddr)}</div>
        <div class="endpoint-label endpoint-start">START LOCATION</div>
      </td>
      <td class="stop-time">${esc(route.route_start_time || '')}</td>
      <td class="stop-status endpoint-start">START</td>
    </tr>` : '';

  const endRow = endAddr ? `
    <tr class="endpoint-row">
      <td class="stop-n endpoint-end">E</td>
      <td>
        <div class="stop-addr">${esc(endAddr)}</div>
        <div class="endpoint-label endpoint-end">${route.return_to_start ? 'RETURN TO START' : 'END LOCATION'}</div>
      </td>
      <td class="stop-time">${esc(route.estimated_end_time || '')}</td>
      <td class="stop-status endpoint-end">END</td>
    </tr>` : '';

  const stopRows = stops.map((s, i) => `
    <tr>
      <td class="stop-n">${i + 1}</td>
      <td>
        <div class="stop-addr">${esc(s.address || '—')}</div>
        ${s.notes ? `<div class="stop-notes">${esc(s.notes)}</div>` : ''}
      </td>
      <td class="stop-time">${esc(s.estimated_arrival || '')}</td>
      <td class="stop-status">${esc((s.status || 'pending').toUpperCase())}</td>
    </tr>`).join('');

  const directionsHtml = directions ? `
    <div class="legs">
      <h2>Turn-by-turn directions</h2>
      ${directions.map((leg) => `
        <div class="leg">
          <div class="leg-header">
            <span class="leg-label">${esc(leg.label || '')}</span>
            <span class="leg-meta">${fmtMi(leg.distance_meters)} · ${fmtDur(leg.duration_seconds)}</span>
          </div>
          <ol class="steps">
            ${(leg.steps || []).map((st) => `
              <li>
                <span class="step-text">${esc(st.instruction || st.name || 'Continue')}</span>
                <span class="step-dist">${fmtMi(st.distance_meters)}</span>
              </li>
            `).join('')}
          </ol>
        </div>
      `).join('')}
    </div>` : '';

  return `
    <div id="servio-route-pdf" style="font-family: ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; color: #1e293b; padding: 0; max-width: 7.5in; margin: 0 auto; background: #fff;">
      <style>
        #servio-route-pdf h1 { margin: 0; font-size: 22px; letter-spacing: -0.02em; color: #0a0f1e; }
        #servio-route-pdf h2 { margin: 18px 0 8px; font-size: 13px; text-transform: uppercase; letter-spacing: 0.12em; color: #1E5BF6; border-bottom: 1px solid #1E5BF640; padding-bottom: 4px; }
        #servio-route-pdf .head { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #1E5BF6; padding-bottom: 10px; margin-bottom: 14px; }
        #servio-route-pdf .head .eyebrow { font-size: 9px; letter-spacing: 0.22em; text-transform: uppercase; color: #1E5BF6; font-weight: 700; margin-bottom: 4px; }
        #servio-route-pdf .head .meta { text-align: right; font-size: 10px; color: #475569; line-height: 1.5; }
        #servio-route-pdf .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 14px; }
        #servio-route-pdf .summary .card { background: #f1f5f9; border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 10px; }
        #servio-route-pdf .summary .label { font-size: 8px; letter-spacing: 0.18em; text-transform: uppercase; color: #64748b; font-weight: 600; }
        #servio-route-pdf .summary .value { font-size: 14px; font-weight: 700; color: #0a0f1e; margin-top: 2px; }
        #servio-route-pdf table.stops { width: 100%; border-collapse: collapse; font-size: 10px; }
        #servio-route-pdf table.stops th { text-align: left; background: #f1f5f9; padding: 6px 8px; border-bottom: 1px solid #cbd5e1; font-size: 9px; letter-spacing: 0.08em; text-transform: uppercase; color: #475569; }
        #servio-route-pdf table.stops td { padding: 6px 8px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
        #servio-route-pdf .stop-n { width: 24px; font-weight: 700; color: #1E5BF6; }
        #servio-route-pdf .stop-addr { font-weight: 600; color: #0a0f1e; }
        #servio-route-pdf .stop-notes { font-size: 9px; color: #64748b; margin-top: 2px; font-style: italic; }
        #servio-route-pdf .stop-time { width: 70px; color: #475569; font-variant-numeric: tabular-nums; }
        #servio-route-pdf .stop-status { width: 60px; font-size: 8px; letter-spacing: 0.08em; color: #475569; }
        #servio-route-pdf .endpoint-row td { background: #f8fafc; }
        #servio-route-pdf .endpoint-label { font-size: 8px; letter-spacing: 0.14em; text-transform: uppercase; font-weight: 700; margin-top: 2px; }
        #servio-route-pdf .endpoint-start { color: #059669 !important; }
        #servio-route-pdf .endpoint-end { color: #dc2626 !important; }
        #servio-route-pdf .legs { margin-top: 18px; }
        #servio-route-pdf .leg { margin-bottom: 14px; page-break-inside: avoid; }
        #servio-route-pdf .leg-header { display: flex; justify-content: space-between; align-items: baseline; border-left: 3px solid #1E5BF6; padding-left: 8px; margin-bottom: 6px; }
        #servio-route-pdf .leg-label { font-weight: 700; font-size: 11px; color: #0a0f1e; }
        #servio-route-pdf .leg-meta { font-size: 9px; color: #64748b; font-variant-numeric: tabular-nums; }
        #servio-route-pdf .steps { list-style: decimal; padding-left: 20px; margin: 0; }
        #servio-route-pdf .steps li { font-size: 10px; padding: 2px 0; color: #334155; display: flex; justify-content: space-between; gap: 10px; }
        #servio-route-pdf .step-text { flex: 1; }
        #servio-route-pdf .step-dist { font-variant-numeric: tabular-nums; color: #64748b; font-size: 9px; white-space: nowrap; }
        #servio-route-pdf .foot { margin-top: 20px; padding-top: 8px; border-top: 1px solid #e2e8f0; font-size: 9px; color: #64748b; display: flex; justify-content: space-between; }
      </style>

      <div class="head">
        <div>
          <div class="eyebrow">◢ ROUTE SHEET</div>
          <h1>${esc(route.name || 'Untitled route')}</h1>
          <div style="font-size: 11px; color: #475569; margin-top: 2px;">${esc(fmtDate(route.date))}</div>
        </div>
        <div class="meta">
          <div style="font-weight: 700; color: #0a0f1e; font-size: 12px;">${esc(server.name || '')}</div>
          ${server.phone ? `<div>${esc(server.phone)}</div>` : ''}
          ${server.email ? `<div>${esc(server.email)}</div>` : ''}
        </div>
      </div>

      <div class="summary">
        <div class="card"><div class="label">Stops</div><div class="value">${stops.length}</div></div>
        <div class="card"><div class="label">Distance</div><div class="value">${route.total_distance ? route.total_distance + ' mi' : '—'}</div></div>
        <div class="card"><div class="label">Duration</div><div class="value">${route.total_duration ? Math.floor(route.total_duration / 60) + 'h ' + (route.total_duration % 60) + 'm' : '—'}</div></div>
        <div class="card"><div class="label">Start</div><div class="value">${esc(route.route_start_time || '—')}</div></div>
      </div>

      <h2>Stops</h2>
      <table class="stops">
        <thead>
          <tr><th>#</th><th>Address / Notes</th><th>ETA</th><th>Status</th></tr>
        </thead>
        <tbody>${startRow}${stopRows}${endRow}</tbody>
      </table>

      ${directionsHtml}

      <div class="foot">
        <span>Generated by Servio · servio.legal</span>
        <span>${new Date().toLocaleString()}</span>
      </div>
    </div>
  `;
}

// Map controller component to fly to focused stop
function MapFocusController({ focusedStopId, stops }) {
  const map = useMap();
  const prevFocusedRef = React.useRef(null);
  
  React.useEffect(() => {
    // Only act if focusedStopId changed and is not null
    if (focusedStopId && focusedStopId !== prevFocusedRef.current && stops && stops.length > 0) {
      prevFocusedRef.current = focusedStopId;
      const stop = stops.find(s => s.id === focusedStopId);
      if (stop && stop.lat && stop.lng) {
        console.log('Flying to stop:', stop.defendant_name, 'at', stop.lat, stop.lng);
        // Use setTimeout to ensure this runs after FitBounds effect
        setTimeout(() => {
          if (map && map.flyTo) {
            map.flyTo([stop.lat, stop.lng], 16, { duration: 0.8 });
          }
        }, 50);
      }
    }
  }, [focusedStopId, stops, map]);
  
  return null;
}

export default function RoutesTab({ profile }) {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [showNewRouteDialog, setShowNewRouteDialog] = useState(false);
  const [showAddStopDialog, setShowAddStopDialog] = useState(false);
  const [showEditStopDialog, setShowEditStopDialog] = useState(false);
  const [editingStop, setEditingStop] = useState(null);
  const [optimizing, setOptimizing] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  
  // File Import state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  
  // Time constraint violations after optimization
  const [timeViolations, setTimeViolations] = useState([]);
  
  // Focused stop for map highlighting
  const [focusedStopId, setFocusedStopId] = useState(null);
  
  // Route Analytics dialog
  const [showAnalyticsDialog, setShowAnalyticsDialog] = useState(false);
  const [analyticsRouteId, setAnalyticsRouteId] = useState(null);
  
  // Add Stop Mid-Route dialog
  const [showAddStopMidRouteDialog, setShowAddStopMidRouteDialog] = useState(false);
  
  // Show archived routes toggle
  const [showArchived, setShowArchived] = useState(false);
  
  // Navigation preference (stored in localStorage)
  const [navPreference, setNavPreference] = useState(() => {
    return localStorage.getItem('servio_nav_preference') || null;
  });
  const [showNavPicker, setShowNavPicker] = useState(false);
  const [navigatingStop, setNavigatingStop] = useState(null);
  
  // Auto-navigate to next stop setting
  const [autoNavigate, setAutoNavigate] = useState(() => {
    return localStorage.getItem('servio_auto_navigate') === 'true';
  });
  
  // Initial route stats (before any stops completed)
  const [initialStats, setInitialStats] = useState({ distance: 0, duration: 0 });
  
  // Address autocomplete state
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingAddress, setSearchingAddress] = useState(false);
  const suggestionTimeoutRef = useRef(null);
  const addressInputRef = useRef(null);
  
  // Edit address autocomplete state (separate from add)
  const [editAddressSuggestions, setEditAddressSuggestions] = useState([]);
  const [showEditSuggestions, setShowEditSuggestions] = useState(false);
  const [editAddressInput, setEditAddressInput] = useState('');
  const editSuggestionTimeoutRef = useRef(null);
  
  // New route form
  const [newRoute, setNewRoute] = useState({
    name: '',
    date: new Date().toISOString().split('T')[0],
    use_current_location: true,
    start_address: '',
    end_address: '',
    return_to_start: false,
    time_per_stop_minutes: 5,
    avoid_tolls: false,
    avoid_highways: false,
    avoid_unpaved: false
  });
  
  // Edit route dialog
  const [showEditRouteDialog, setShowEditRouteDialog] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showPdfDialog, setShowPdfDialog] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [pdfIncludeDirections, setPdfIncludeDirections] = useState(true);
  const [templateRoutes, setTemplateRoutes] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  // Load templates when picker opens
  useEffect(() => {
    if (!showTemplatePicker) return;
    let active = true;
    (async () => {
      setTemplatesLoading(true);
      try {
        const { data } = await api.listRouteTemplates();
        if (active) setTemplateRoutes(data.templates || []);
      } catch (err) {
        if (active) toast.error('Failed to load templates');
      } finally {
        if (active) setTemplatesLoading(false);
      }
    })();
    return () => { active = false; };
  }, [showTemplatePicker]);

  const handleCreateFromTemplate = async (templateId) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const { data } = await api.duplicateRoute(templateId, { new_date: today });
      // Strip the template flag from the new route (duplicate already does this)
      setRoutes([data, ...routes]);
      setSelectedRoute(data);
      setRouteDirections(null);
      setShowTemplatePicker(false);
      toast.success('Route created from template');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create from template');
    }
  };
  const [editingRouteGeocoding, setEditingRouteGeocoding] = useState(false);
  
  // Route directions and map
  const [routeDirections, setRouteDirections] = useState(null);
  const [loadingDirections, setLoadingDirections] = useState(false);
  const [showMap, setShowMap] = useState(true);
  
  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  
  // Selected address coords for stop
  const [selectedAddressCoords, setSelectedAddressCoords] = useState({ lat: null, lng: null });
  
  // New stop form
  const [newStop, setNewStop] = useState({
    address: '',
    defendant_name: '',
    case_number: '',
    notes: '',
    priority: 'normal'
  });

  // Format date as "February 20, 2026" for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr + 'T00:00:00');
      return date.toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  // Format minutes as hours and minutes (e.g., 93 -> "1h 33m")
  const formatDuration = (totalMinutes) => {
    if (!totalMinutes || totalMinutes <= 0) return '0m';
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60);
    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  };

  // Format 24h time (HH:MM) to 12h format (h:MM AM/PM)
  const formatTime12h = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
  };

  // Check if a scheduled time (HH:MM) is in the future
  const isScheduledTimeInFuture = (scheduledTime) => {
    if (!scheduledTime) return false;
    try {
      const [schedHour, schedMin] = scheduledTime.split(':').map(Number);
      const now = new Date();
      const scheduledMins = schedHour * 60 + schedMin;
      const currentMins = now.getHours() * 60 + now.getMinutes();
      return scheduledMins > currentMins;
    } catch {
      return false;
    }
  };

  // Address autocomplete for route start/end
  const [startAddressSuggestions, setStartAddressSuggestions] = useState([]);
  const [endAddressSuggestions, setEndAddressSuggestions] = useState([]);
  const [showStartSuggestions, setShowStartSuggestions] = useState(false);
  const [showEndSuggestions, setShowEndSuggestions] = useState(false);
  const [searchingStartAddress, setSearchingStartAddress] = useState(false);
  const [searchingEndAddress, setSearchingEndAddress] = useState(false);
  const [startAddressVerified, setStartAddressVerified] = useState(false);
  const [endAddressVerified, setEndAddressVerified] = useState(false);
  const startAddressTimeoutRef = useRef(null);
  const endAddressTimeoutRef = useRef(null);

  const fetchRoutes = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 50 };
      if (showArchived) {
        params.status = 'archived';
      }
      const response = await api.getRoutes(params);
      setRoutes(response.data.routes || []);
    } catch (error) {
      toast.error('Failed to load routes');
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  // Initialize edit address input when editing a stop
  useEffect(() => {
    if (editingStop && showEditStopDialog) {
      setEditAddressInput(editingStop.address || '');
    }
  }, [editingStop, showEditStopDialog]);

  // Fetch directions when route is selected
  const fetchDirections = useCallback(async (routeId) => {
    if (!routeId) return;
    setLoadingDirections(true);
    try {
      const response = await api.getRouteDirections(routeId);
      setRouteDirections(response.data);
      
      // Update the routes list with accurate distance and duration
      if (response.data.distance_miles !== undefined) {
        setRoutes(prevRoutes => prevRoutes.map(r => 
          r.id === routeId 
            ? { ...r, total_distance: response.data.distance_miles, total_duration: response.data.duration_minutes }
            : r
        ));
        // Also update selectedRoute if it matches
        setSelectedRoute(prev => 
          prev?.id === routeId 
            ? { ...prev, total_distance: response.data.distance_miles, total_duration: response.data.duration_minutes }
            : prev
        );
      }
    } catch (error) {
      console.error('Failed to fetch directions:', error);
      setRouteDirections(null);
    } finally {
      setLoadingDirections(false);
    }
  }, []);

  // Calculate remaining stats when route changes
  useEffect(() => {
    if (selectedRoute?.stops) {
      const pendingStops = selectedRoute.stops.filter(s => s.status === 'pending');
      const totalCount = selectedRoute.stops.length;
      const pendingCount = pendingStops.length;
      
      // Use backend-stored remaining values if available (most accurate)
      // Only fall back to calculation if backend values don't exist
      // Treat remaining_distance > 0 as valid, OR 0 with no pending stops (completed route)
      const hasValidRemaining = selectedRoute.remaining_distance !== undefined 
        && selectedRoute.remaining_distance !== null
        && (selectedRoute.remaining_distance > 0 || pendingCount === 0);
      if (hasValidRemaining) {
        // Backend values exist and are valid, don't override
        return;
      }
      
      // Fallback: Calculate remaining based on pending stops (less accurate)
      const percentRemaining = totalCount > 0 ? pendingCount / totalCount : 0;
      const initialDist = selectedRoute.initial_distance || selectedRoute.total_distance || 0;
      const initialDur = selectedRoute.initial_duration || selectedRoute.total_duration || 0;
      
      let remainingDist = Math.round(initialDist * percentRemaining * 10) / 10;
      let remainingDur = Math.round(initialDur * percentRemaining);
      
      // Add the final leg distance/time if there's an ending location
      if (selectedRoute.return_to_start || selectedRoute.end_address) {
        const finalLegDistance = selectedRoute.end_distance || initialDist * 0.1;
        const finalLegDuration = selectedRoute.end_duration || 10;
        remainingDist = Math.round((remainingDist + finalLegDistance) * 10) / 10;
        remainingDur = Math.round(remainingDur + finalLegDuration);
      }
      
      setSelectedRoute(prev => ({
        ...prev,
        remaining_distance: remainingDist,
        remaining_duration: remainingDur
      }));
    }
  }, [selectedRoute?.stops?.filter(s => s.status !== 'pending').length]);

  // Handle route selection
  const handleSelectRoute = useCallback((route) => {
    setSelectedRoute(route);
    setRouteDirections(null);
    if (route?.stops?.length > 0) {
      fetchDirections(route.id);
    }
  }, [fetchDirections]);

  // Also fetch directions whenever selectedRoute changes (backup)
  useEffect(() => {
    if (selectedRoute?.id && selectedRoute?.stops?.length > 0 && !routeDirections && !loadingDirections) {
      console.log('Fetching directions for route:', selectedRoute.id, 'stops:', selectedRoute.stops.length);
      fetchDirections(selectedRoute.id);
    }
  }, [selectedRoute?.id, selectedRoute?.stops?.length, routeDirections, loadingDirections, fetchDirections]);

  // Re-optimize remaining pending stops
  const handleReoptimizeRemaining = async () => {
    if (!selectedRoute) return;
    
    const pendingStops = selectedRoute.stops.filter(s => s.status === 'pending');
    if (pendingStops.length < 2) {
      toast.error('Need at least 2 pending stops to re-optimize');
      return;
    }
    
    setOptimizing(true);
    try {
      // Get current position if using current location
      let currentLat = null, currentLng = null;
      if (selectedRoute.use_current_location && navigator.geolocation) {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
        }).catch(() => null);
        if (pos) {
          currentLat = pos.coords.latitude;
          currentLng = pos.coords.longitude;
        }
      }
      
      // Get current time in HH:MM format
      const now = new Date();
      const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
      
      const response = await api.optimizeRoute(selectedRoute.id, currentLat, currentLng, currentTime);
      
      // Update the route with optimized stops
      const updatedRoute = { ...selectedRoute, stops: response.data.stops, total_distance: response.data.total_distance };
      setSelectedRoute(updatedRoute);
      setRoutes(routes.map(r => r.id === updatedRoute.id ? updatedRoute : r));
      
      // Refresh directions
      fetchDirections(selectedRoute.id);
      
      toast.success('Route re-optimized!');
    } catch (error) {
      toast.error('Failed to re-optimize route');
    } finally {
      setOptimizing(false);
    }
  };
  
  // Fetch address suggestions with debounce
  const fetchAddressSuggestions = useCallback(async (query) => {
    if (query.length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    setSearchingAddress(true);
    try {
      const response = await api.getAddressSuggestions(query);
      setAddressSuggestions(response.data.suggestions || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
      setAddressSuggestions([]);
    } finally {
      setSearchingAddress(false);
    }
  }, []);
  
  const handleAddressInputChange = (value) => {
    setNewStop({ ...newStop, address: value });
    // Clear coords when user types - they'll be re-set when they select a suggestion
    // If they don't select a suggestion, backend will geocode
    setSelectedAddressCoords({ lat: null, lng: null });
    
    // Debounce the API call
    if (suggestionTimeoutRef.current) {
      clearTimeout(suggestionTimeoutRef.current);
    }
    
    suggestionTimeoutRef.current = setTimeout(() => {
      fetchAddressSuggestions(value);
    }, 300);
  };
  
  const handleSelectSuggestion = (suggestion) => {
    setNewStop({ ...newStop, address: suggestion.address });
    setSelectedAddressCoords({ lat: suggestion.lat, lng: suggestion.lng });
    setShowSuggestions(false);
    setAddressSuggestions([]);
  };

  // Edit address autocomplete handlers (separate state for performance)
  const handleEditAddressInputChange = (value) => {
    setEditAddressInput(value);
    
    // Debounce the API call
    if (editSuggestionTimeoutRef.current) {
      clearTimeout(editSuggestionTimeoutRef.current);
    }
    
    if (value.length < 3) {
      setEditAddressSuggestions([]);
      setShowEditSuggestions(false);
      return;
    }
    
    editSuggestionTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await api.getAddressSuggestions(value);
        setEditAddressSuggestions(response.data.suggestions || []);
        setShowEditSuggestions(true);
      } catch (error) {
        setEditAddressSuggestions([]);
      }
    }, 300);
  };
  
  const handleSelectEditSuggestion = (suggestion) => {
    setEditAddressInput(suggestion.address);
    if (editingStop) {
      setEditingStop({ 
        ...editingStop, 
        address: suggestion.address,
        latitude: suggestion.lat,
        longitude: suggestion.lng,
        _addressChanged: true,
        _newCoords: { lat: suggestion.lat, lng: suggestion.lng }
      });
    }
    setShowEditSuggestions(false);
    setEditAddressSuggestions([]);
  };

  // Start address autocomplete handlers
  const handleStartAddressChange = (value, isNewRoute = true) => {
    if (isNewRoute) {
      setNewRoute({ ...newRoute, start_address: value });
    } else {
      setEditingRoute({ ...editingRoute, start_address: value });
    }
    setStartAddressVerified(false);
    
    if (startAddressTimeoutRef.current) {
      clearTimeout(startAddressTimeoutRef.current);
    }
    
    if (value.length < 3) {
      setStartAddressSuggestions([]);
      setShowStartSuggestions(false);
      return;
    }
    
    startAddressTimeoutRef.current = setTimeout(async () => {
      setSearchingStartAddress(true);
      try {
        const response = await api.getAddressSuggestions(value);
        setStartAddressSuggestions(response.data.suggestions || []);
        setShowStartSuggestions(true);
      } catch (error) {
        console.error('Failed to fetch start address suggestions:', error);
        setStartAddressSuggestions([]);
      } finally {
        setSearchingStartAddress(false);
      }
    }, 300);
  };

  const handleSelectStartSuggestion = (suggestion, isNewRoute = true) => {
    if (isNewRoute) {
      setNewRoute({ ...newRoute, start_address: suggestion.address });
    } else {
      setEditingRoute({ ...editingRoute, start_address: suggestion.address });
    }
    setStartAddressVerified(true);
    setShowStartSuggestions(false);
    setStartAddressSuggestions([]);
  };

  // End address autocomplete handlers
  const handleEndAddressChange = (value, isNewRoute = true) => {
    if (isNewRoute) {
      setNewRoute({ ...newRoute, end_address: value });
    } else {
      setEditingRoute({ ...editingRoute, end_address: value });
    }
    setEndAddressVerified(false);
    
    if (endAddressTimeoutRef.current) {
      clearTimeout(endAddressTimeoutRef.current);
    }
    
    if (value.length < 3) {
      setEndAddressSuggestions([]);
      setShowEndSuggestions(false);
      return;
    }
    
    endAddressTimeoutRef.current = setTimeout(async () => {
      setSearchingEndAddress(true);
      try {
        const response = await api.getAddressSuggestions(value);
        setEndAddressSuggestions(response.data.suggestions || []);
        setShowEndSuggestions(true);
      } catch (error) {
        console.error('Failed to fetch end address suggestions:', error);
        setEndAddressSuggestions([]);
      } finally {
        setSearchingEndAddress(false);
      }
    }, 300);
  };

  const handleSelectEndSuggestion = (suggestion, isNewRoute = true) => {
    if (isNewRoute) {
      setNewRoute({ ...newRoute, end_address: suggestion.address });
    } else {
      setEditingRoute({ ...editingRoute, end_address: suggestion.address });
    }
    setEndAddressVerified(true);
    setShowEndSuggestions(false);
    setEndAddressSuggestions([]);
  };

  const handleCreateRoute = async () => {
    if (!newRoute.name.trim()) {
      toast.error('Please enter a route name');
      return;
    }
    
    try {
      // Geocode start address if provided
      let startLat = null, startLng = null;
      if (newRoute.start_address && !newRoute.use_current_location) {
        setGeocoding(true);
        try {
          const geoRes = await api.geocodeAddress(newRoute.start_address);
          startLat = geoRes.data.lat;
          startLng = geoRes.data.lng;
        } catch {
          toast.error('Could not geocode start address');
        }
      }
      
      // Geocode end address if provided (only if not returning to start)
      let endLat = null, endLng = null;
      let endAddress = newRoute.end_address;
      
      if (newRoute.return_to_start) {
        // Use start address as end address
        endAddress = newRoute.start_address || null;
        endLat = startLat;
        endLng = startLng;
      } else if (newRoute.end_address) {
        try {
          const geoRes = await api.geocodeAddress(newRoute.end_address);
          endLat = geoRes.data.lat;
          endLng = geoRes.data.lng;
        } catch {
          toast.error('Could not geocode end address');
        }
      }
      setGeocoding(false);
      
      const response = await api.createRoute({
        name: newRoute.name,
        date: newRoute.date,
        use_current_location: newRoute.use_current_location,
        start_address: newRoute.start_address || null,
        start_lat: startLat,
        start_lng: startLng,
        end_address: endAddress || null,
        end_lat: endLat,
        end_lng: endLng,
        return_to_start: newRoute.return_to_start,
        time_per_stop_minutes: newRoute.time_per_stop_minutes,
        avoid_tolls: newRoute.avoid_tolls,
        avoid_highways: newRoute.avoid_highways,
        avoid_unpaved: newRoute.avoid_unpaved,
        stops: []
      });
      
      setRoutes([response.data, ...routes]);
      setSelectedRoute(response.data);
      setRouteDirections(null); // Clear previous route's map data
      setShowNewRouteDialog(false);
      setNewRoute({
        name: '',
        date: new Date().toISOString().split('T')[0],
        use_current_location: true,
        start_address: '',
        end_address: '',
        return_to_start: false,
        time_per_stop_minutes: 5,
        avoid_tolls: false,
        avoid_highways: false,
        avoid_unpaved: false
      });
      toast.success('Route created');
    } catch (error) {
      toast.error('Failed to create route');
    }
  };

  // File Import Handlers
  const handleFileDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleFileSelect = (file) => {
    const validTypes = ['.csv', '.xlsx', '.xls'];
    const fileName = file.name.toLowerCase();
    if (!validTypes.some(ext => fileName.endsWith(ext))) {
      toast.error('Please upload a CSV or Excel file (.csv, .xlsx, .xls)');
      return;
    }
    setImportFile(file);
    setImportPreview(null);
    handleImportFile(file);
  };

  const handleImportFile = async (file) => {
    setImporting(true);
    try {
      const response = await api.importStopsFromFile(file);
      setImportPreview(response.data);
      if (response.data.successful > 0) {
        toast.success(`Parsed ${response.data.successful} addresses successfully`);
      }
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} addresses could not be geocoded`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to parse file');
      setImportPreview(null);
    }
    setImporting(false);
  };

  const handleConfirmImport = async () => {
    if (!importPreview || !importPreview.stops.length) return;
    
    if (!selectedRoute) {
      toast.error('Please select or create a route first');
      return;
    }
    
    try {
      // Add all imported stops to the current route
      const updatedStops = [...(selectedRoute.stops || []), ...importPreview.stops.map(stop => ({
        id: stop.id,
        address: stop.address,
        latitude: stop.latitude,
        longitude: stop.longitude,
        defendant_name: stop.defendant_name,
        case_number: stop.case_number,
        notes: stop.notes,
        priority: stop.priority || 'normal',
        status: 'pending',
        time_constraint_type: stop.time_constraint_type || 'none',
        time_window_start: stop.time_window_start,
        time_window_end: stop.time_window_end
      }))];
      
      await api.updateRoute(selectedRoute.id, { stops: updatedStops });
      
      // Refresh route
      const updatedRoute = await api.getRoute(selectedRoute.id);
      setSelectedRoute(updatedRoute.data);
      setRoutes(routes.map(r => r.id === selectedRoute.id ? updatedRoute.data : r));
      
      toast.success(`Added ${importPreview.stops.length} stops to route`);
      setShowImportDialog(false);
      setImportFile(null);
      setImportPreview(null);
    } catch (error) {
      toast.error('Failed to add stops to route');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.downloadImportTemplate();
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Route Stops Example.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleAddStop = async () => {
    if (!newStop.address.trim()) {
      toast.error('Please enter an address');
      return;
    }
    
    if (!selectedRoute) return;
    
    try {
      setGeocoding(true);
      
      // Use pre-selected coordinates if available, otherwise geocode
      let lat = selectedAddressCoords.lat;
      let lng = selectedAddressCoords.lng;
      let address = newStop.address;
      
      if (!lat || !lng) {
        try {
          const geoRes = await api.geocodeAddress(newStop.address);
          lat = geoRes.data.lat;
          lng = geoRes.data.lng;
          address = geoRes.data.address; // Use formatted address
        } catch {
          toast.warning('Could not geocode address - stop added without coordinates');
        }
      }
      
      const stop = {
        id: crypto.randomUUID(),
        address: address,
        lat,
        lng,
        defendant_name: newStop.defendant_name || null,
        case_number: newStop.case_number || null,
        notes: newStop.notes || null,
        priority: newStop.priority,
        status: 'pending',
        order: (selectedRoute.stops?.length || 0) + 1
      };
      
      console.log('Adding stop with coordinates:', { lat, lng, address });
      
      const updatedStops = [...(selectedRoute.stops || []), stop];
      
      console.log('All stops being sent:', updatedStops.map(s => ({ id: s.id?.slice(0,8), lat: s.lat, lng: s.lng })));
      
      const response = await api.updateRoute(selectedRoute.id, { stops: updatedStops });
      setSelectedRoute(response.data);
      setRoutes(routes.map(r => r.id === response.data.id ? response.data : r));
      
      // Small delay to ensure database is fully updated before fetching directions
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Refresh directions to update map route lines
      fetchDirections(selectedRoute.id);
      
      setShowAddStopDialog(false);
      setNewStop({
        address: '',
        defendant_name: '',
        case_number: '',
        notes: '',
        priority: 'normal'
      });
      setSelectedAddressCoords({ lat: null, lng: null });
      setAddressSuggestions([]);
      toast.success('Stop added');
    } catch (error) {
      toast.error('Failed to add stop');
    } finally {
      setGeocoding(false);
    }
  };

  const handleUpdateStop = async (stopId, updates) => {
    if (!selectedRoute) return;
    
    const updatedStops = selectedRoute.stops.map(s => 
      s.id === stopId ? { ...s, ...updates } : s
    );
    
    try {
      const response = await api.updateRoute(selectedRoute.id, { stops: updatedStops });
      setSelectedRoute(response.data);
      setRoutes(routes.map(r => r.id === response.data.id ? response.data : r));
    } catch (error) {
      toast.error('Failed to update stop');
    }
  };

  const handleEditStop = (stop) => {
    setEditingStop({ ...stop });
    setShowEditStopDialog(true);
  };

  const handleSaveEditedStop = async () => {
    if (!editingStop || !selectedRoute) return;
    
    // Validate time window for "arrive_between" constraint
    if (editingStop.time_constraint_type === 'arrive_between') {
      const start = editingStop.time_window_start;
      const end = editingStop.time_window_end;
      
      if (!start || !end) {
        toast.error('Please enter both window start and end times');
        return;
      }
      
      // Parse times to compare
      const [startH, startM] = start.split(':').map(Number);
      const [endH, endM] = end.split(':').map(Number);
      const startMins = startH * 60 + startM;
      const endMins = endH * 60 + endM;
      
      // If end is before start by less than 2 hours, it's likely a user error
      // (e.g., 10:08 PM to 10:00 PM is probably wrong, not an overnight window)
      if (endMins < startMins && (startMins - endMins) < 120) {
        toast.error('Window end time must be after start time. For overnight windows, the end time should be at least 2 hours before the start time.');
        return;
      }
      
      // If times are exactly the same, that's an error
      if (startMins === endMins) {
        toast.error('Window start and end times cannot be the same');
        return;
      }
    }
    
    try {
      let updatedData = {
        defendant_name: editingStop.defendant_name,
        case_number: editingStop.case_number,
        notes: editingStop.notes,
        priority: editingStop.priority,
        time_constraint_type: editingStop.time_constraint_type || 'none',
        time_constraint_time: editingStop.time_constraint_time || null,
        time_window_start: editingStop.time_window_start || null,
        time_window_end: editingStop.time_window_end || null,
        service_time_minutes: editingStop.service_time_minutes || null
      };
      
      // If address was changed via autocomplete, use pre-geocoded coords
      if (editingStop._addressChanged && editingStop._newCoords) {
        updatedData.address = editingStop.address;
        // Use 'lat' and 'lng' to match the stop object format used by optimization
        updatedData.lat = editingStop._newCoords.lat;
        updatedData.lng = editingStop._newCoords.lng;
      } else {
        // Check if address was manually typed (different from original)
        const originalStop = selectedRoute.stops.find(s => s.id === editingStop.id);
        if (originalStop && editAddressInput && editAddressInput !== originalStop.address) {
          setGeocoding(true);
          try {
            const geocodeResponse = await api.geocodeAddress(editAddressInput);
            updatedData.address = geocodeResponse.data.address;
            // Use 'lat' and 'lng' to match the stop object format used by optimization
            updatedData.lat = geocodeResponse.data.lat;
            updatedData.lng = geocodeResponse.data.lng;
          } catch (geocodeError) {
            toast.error('Could not find that address. Please try again.');
            setGeocoding(false);
            return;
          }
          setGeocoding(false);
        }
      }
      
      await handleUpdateStop(editingStop.id, updatedData);
      setShowEditStopDialog(false);
      setEditingStop(null);
      setEditAddressInput('');
      setEditAddressSuggestions([]);
      toast.success('Stop updated');
    } catch (error) {
      toast.error('Failed to update stop');
    }
  };

  const handleDeleteStop = async (stopId) => {
    if (!selectedRoute) return;
    
    // Find the stop to show in confirmation
    const stopToDelete = selectedRoute.stops.find(s => s.id === stopId);
    const confirmMessage = stopToDelete?.defendant_name 
      ? `Remove stop for ${stopToDelete.defendant_name}?`
      : 'Remove this stop?';
    
    if (!window.confirm(confirmMessage)) return;
    
    const updatedStops = selectedRoute.stops.filter(s => s.id !== stopId);
    
    try {
      const response = await api.updateRoute(selectedRoute.id, { stops: updatedStops });
      setSelectedRoute(response.data);
      setRoutes(routes.map(r => r.id === response.data.id ? response.data : r));
      toast.success('Stop removed');
    } catch (error) {
      toast.error('Failed to remove stop');
    }
  };

  const handleResetRoute = async () => {
    if (!selectedRoute) return;
    
    // Check if ALL stops are in a final status (route fully complete)
    const totalStops = selectedRoute.stops?.length || 0;
    const completedCount = selectedRoute.stops?.filter(s => 
      ['completed', 'successful', 'unsuccessful', 'skipped'].includes(s.status)
    ).length || 0;
    
    if (totalStops === 0) {
      toast.info('No stops to reset');
      return;
    }
    
    if (completedCount < totalStops) {
      toast.info('Reset is only available after all stops are completed');
      return;
    }
    
    if (!window.confirm(`Reset all ${completedCount} stops back to pending?\n\nThis is useful for repeating routes (e.g., weekly deliveries, law firm visits).`)) {
      return;
    }
    
    try {
      const response = await api.resetRoute(selectedRoute.id);
      setSelectedRoute(response.data.route);
      setRoutes(routes.map(r => r.id === response.data.route.id ? response.data.route : r));
      toast.success(`Reset ${response.data.reset_count} stops to pending`);
      
      // Refresh directions
      fetchDirections(selectedRoute.id);
    } catch (error) {
      toast.error('Failed to reset route');
    }
  };

  // PDF Export handler
  const handleExportPdf = async () => {
    if (!selectedRoute) return;
    
    try {
      const response = await api.exportRoutePdf(selectedRoute.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedRoute.name || 'Route'} - ${selectedRoute.date || 'export'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  // Share Route handler — opens the share dialog (visibility toggles + copyable link)
  const handleShareRoute = async () => {
    if (!selectedRoute) return;
    try {
      // Make sure a share token exists, then open the dialog
      const response = await api.createShareLink(selectedRoute.id);
      const updated = {
        ...selectedRoute,
        share_token: response.data.share_token,
        // Pre-populate visibility defaults if missing (all ON)
        share_show_map:          selectedRoute.share_show_map          ?? true,
        share_show_addresses:    selectedRoute.share_show_addresses    ?? true,
        share_show_other_stops:  selectedRoute.share_show_other_stops  ?? true,
        share_show_driver_name:  selectedRoute.share_show_driver_name  ?? true,
        share_show_eta:          selectedRoute.share_show_eta          ?? true,
        share_show_progress:     selectedRoute.share_show_progress     ?? true,
      };
      setSelectedRoute(updated);
      setRoutes(routes.map(r => r.id === selectedRoute.id ? { ...r, share_token: response.data.share_token } : r));
      setShowShareDialog(true);
    } catch (error) {
      toast.error('Failed to open share settings');
    }
  };

  const updateShareVisibility = async (key, value) => {
    if (!selectedRoute) return;
    const optimistic = { ...selectedRoute, [key]: value };
    setSelectedRoute(optimistic);
    try {
      await api.updateShareVisibility(selectedRoute.id, { [key]: value });
    } catch (error) {
      // Revert on failure
      setSelectedRoute(selectedRoute);
      toast.error('Failed to save visibility setting');
    }
  };

  const copyShareLink = async () => {
    if (!selectedRoute?.share_token) return;
    const url = `${window.location.origin}/track/${selectedRoute.share_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Tracking link copied to clipboard');
    } catch {
      toast.error('Could not copy — select and copy manually');
    }
  };

  const handleMoveStop = async (stopId, direction) => {
    if (!selectedRoute) return;
    
    const stops = [...selectedRoute.stops];
    const index = stops.findIndex(s => s.id === stopId);
    
    if (direction === 'up' && index > 0) {
      [stops[index], stops[index - 1]] = [stops[index - 1], stops[index]];
    } else if (direction === 'down' && index < stops.length - 1) {
      [stops[index], stops[index + 1]] = [stops[index + 1], stops[index]];
    } else {
      return;
    }
    
    // Update order numbers
    stops.forEach((s, i) => s.order = i + 1);
    
    try {
      const response = await api.updateRoute(selectedRoute.id, { stops });
      setSelectedRoute(response.data);
      setRoutes(routes.map(r => r.id === response.data.id ? response.data : r));
    } catch (error) {
      toast.error('Failed to reorder stops');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id || !selectedRoute) return;
    
    const oldIndex = selectedRoute.stops.findIndex(s => s.id === active.id);
    const newIndex = selectedRoute.stops.findIndex(s => s.id === over.id);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    const newStops = arrayMove([...selectedRoute.stops], oldIndex, newIndex);
    
    // Update order numbers
    newStops.forEach((s, i) => s.order = i + 1);
    
    // Optimistic update
    const updatedRoute = { ...selectedRoute, stops: newStops };
    setSelectedRoute(updatedRoute);
    setRoutes(routes.map(r => r.id === updatedRoute.id ? updatedRoute : r));
    
    try {
      const response = await api.updateRoute(selectedRoute.id, { stops: newStops });
      setSelectedRoute(response.data);
      setRoutes(routes.map(r => r.id === response.data.id ? response.data : r));
      // Refresh directions after reordering (uses new segment data from backend)
      fetchDirections(selectedRoute.id);
      toast.success('Stops reordered - distances and ETAs updated!');
    } catch (error) {
      toast.error('Failed to reorder stops');
      // Revert on error
      setSelectedRoute(selectedRoute);
    }
  };

  const handleOptimize = async () => {
    if (!selectedRoute || selectedRoute.stops.length < 2) {
      toast.error('Need at least 2 stops to optimize');
      return;
    }
    
    setOptimizing(true);
    
    try {
      let currentLat = null, currentLng = null;
      
      // Get current location if needed
      if (selectedRoute.use_current_location && navigator.geolocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000 });
          });
          currentLat = position.coords.latitude;
          currentLng = position.coords.longitude;
        } catch {
          toast.warning('Could not get current location, using start address');
        }
      }
      
      // Get current time in HH:MM format
      const now = new Date();
      const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
      
      const response = await api.optimizeRoute(selectedRoute.id, currentLat, currentLng, currentTime);
      
      // Store initial stats (before any stops completed)
      const initialDistance = response.data.total_distance || 0;
      const initialDuration = response.data.total_duration || 0;
      setInitialStats({ distance: initialDistance, duration: initialDuration });
      
      setSelectedRoute(prev => ({
        ...prev,
        stops: response.data.stops,
        total_distance: response.data.total_distance,
        total_duration: response.data.total_duration,
        remaining_distance: response.data.remaining_distance ?? response.data.total_distance,
        remaining_duration: response.data.remaining_duration ?? response.data.total_duration,
        initial_distance: initialDistance,
        initial_duration: initialDuration,
        route_geometry: response.data.route_geometry || prev.route_geometry
      }));
      
      setRoutes(routes.map(r => r.id === selectedRoute.id ? {
        ...r,
        stops: response.data.stops,
        total_distance: response.data.total_distance,
        total_duration: response.data.total_duration,
        remaining_distance: response.data.remaining_distance ?? response.data.total_distance,
        remaining_duration: response.data.remaining_duration ?? response.data.total_duration,
        initial_distance: initialDistance,
        initial_duration: initialDuration,
        route_geometry: response.data.route_geometry || r.route_geometry
      } : r));
      
      // Store time constraint violations for visual indicators
      setTimeViolations(response.data.time_constraint_violations || []);
      
      // Refresh directions with accurate distance/time
      fetchDirections(selectedRoute.id);
      
      // Show constraint violations if any
      if (response.data.violation_count > 0) {
        toast.warning(`Route optimized with ${response.data.violation_count} time constraint conflict(s)`);
      } else {
        toast.success('Route Optimized!');
      }
    } catch (error) {
      toast.error('Failed to optimize route');
    } finally {
      setOptimizing(false);
    }
  };

  const handleReverseRoute = async () => {
    if (!selectedRoute || !selectedRoute.stops || selectedRoute.stops.length < 2) return;
    
    setOptimizing(true);
    try {
      // Reverse the stops array and swap FIRST/LAST priorities
      const reversedStops = [...selectedRoute.stops].reverse().map(stop => {
        // Swap first <-> last priority since direction is reversed
        let newPriority = stop.priority;
        if (stop.priority === 'first') {
          newPriority = 'last';
        } else if (stop.priority === 'last') {
          newPriority = 'first';
        }
        return { ...stop, priority: newPriority };
      });
      
      // Update the route with reversed stops
      // The backend will automatically recalculate distances via Mapbox
      const response = await api.updateRoute(selectedRoute.id, { stops: reversedStops });
      
      setSelectedRoute(response.data);
      setRoutes(routes.map(r => r.id === response.data.id ? response.data : r));
      
      // Refresh directions for the map
      fetchDirections(selectedRoute.id);
      
      toast.success('Route reversed! Distances and ETAs updated.');
    } catch (error) {
      toast.error('Failed to reverse route');
    } finally {
      setOptimizing(false);
    }
  };

  // Start Route - commits to execution mode
  const handleStartRoute = async () => {
    if (!selectedRoute) return;
    
    try {
      const response = await api.startRoute(selectedRoute.id);
      const updatedRoute = response.data;
      
      setSelectedRoute(updatedRoute);
      setRoutes(routes.map(r => r.id === updatedRoute.id ? updatedRoute : r));
      
      toast.success('Route started! Navigate to your first stop.');
      
      // Auto-open navigation to first stop
      const firstPendingStop = updatedRoute.stops?.find(s => s.status === 'pending' || !s.status);
      if (firstPendingStop && firstPendingStop.lat && firstPendingStop.lng) {
        window.open(
          `https://www.google.com/maps/dir/?api=1&destination=${firstPendingStop.lat},${firstPendingStop.lng}`,
          '_blank'
        );
      }
    } catch (error) {
      toast.error('Failed to start route');
      console.error(error);
    }
  };

  const handleCompleteStop = async (stopId, status = 'completed', shouldAutoNavigate = false) => {
    if (!selectedRoute) return;
    
    try {
      // Try to get current GPS location for mileage tracking
      let actualLat = null;
      let actualLng = null;
      
      try {
        if (navigator.geolocation) {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 5000,
              maximumAge: 0
            });
          });
          actualLat = position.coords.latitude;
          actualLng = position.coords.longitude;
        }
      } catch (geoError) {
        // GPS not available or denied - continue without it
        console.log('GPS not available for mileage tracking:', geoError.message);
      }
      
      // Call backend with GPS coordinates for mileage tracking
      const response = await api.completeStop(selectedRoute.id, stopId, status, null, actualLat, actualLng);
      const backendResult = response.data;
      
      // Use the stops from backend if available (has updated ETAs), otherwise update locally
      const updatedStops = backendResult.stops || selectedRoute.stops.map(s => 
        s.id === stopId ? { 
          ...s, 
          status, 
          completed_at: ['completed', 'successful', 'unsuccessful'].includes(status) 
            ? new Date().toISOString() 
            : null 
        } : s
      );
      
      const allDone = updatedStops.every(s => 
        ['completed', 'successful', 'unsuccessful', 'skipped'].includes(s.status)
      );
      
      // Use backend-calculated remaining values (accurate based on actual segment distances)
      const remainingDistanceCalc = backendResult.remaining_distance ?? selectedRoute.remaining_distance ?? 0;
      const remainingDurationCalc = backendResult.remaining_duration ?? selectedRoute.remaining_duration ?? 0;
      const estimatedEndTime = backendResult.estimated_end_time ?? selectedRoute.estimated_end_time;
      
      setSelectedRoute(prev => ({
        ...prev,
        stops: updatedStops,
        status: allDone ? 'completed' : 'in_progress',
        remaining_distance: remainingDistanceCalc,
        remaining_duration: remainingDurationCalc,
        estimated_end_time: estimatedEndTime
      }));
      
      setRoutes(routes.map(r => r.id === selectedRoute.id ? {
        ...r,
        stops: updatedStops,
        status: allDone ? 'completed' : 'in_progress',
        remaining_distance: remainingDistanceCalc,
        remaining_duration: remainingDurationCalc,
        estimated_end_time: estimatedEndTime
      } : r));
      
      // Show appropriate toast based on status
      const statusMessages = {
        'successful': 'Marked as Served!',
        'unsuccessful': 'Marked as Failed Attempt',
        'completed': 'Stop completed!',
        'skipped': 'Stop skipped',
        'pending': 'Stop reset to pending'
      };
      toast.success(statusMessages[status] || 'Status updated');
      
      // Auto-navigate to next stop if enabled and there's a next stop
      if (shouldAutoNavigate && status !== 'pending') {
        const nextStop = updatedStops.find(s => s.status === 'pending');
        if (nextStop) {
          // Small delay to let the toast show
          setTimeout(() => {
            handleNavigateToStop(nextStop);
          }, 500);
        } else if (allDone) {
          // Check if there's a return/end address to navigate to
          if (selectedRoute.return_to_start && selectedRoute.start_address) {
            setTimeout(() => {
              const url = generateNavigationUrl(selectedRoute.start_address, navPreference || 'google');
              if (url) {
                toast.info('Navigating to start location...');
                window.open(url, '_blank');
              }
            }, 500);
          } else if (selectedRoute.end_address) {
            setTimeout(() => {
              const url = generateNavigationUrl(selectedRoute.end_address, navPreference || 'google');
              if (url) {
                toast.info('Navigating to end location...');
                window.open(url, '_blank');
              }
            }, 500);
          } else {
            toast.success('All stops completed! Route finished.');
          }
        }
      }
    } catch (error) {
      toast.error('Failed to update stop status');
    }
  };

  const handleDeleteRoute = async (routeId) => {
    if (!confirm('Delete this route?')) return;
    
    try {
      await api.deleteRoute(routeId);
      setRoutes(routes.filter(r => r.id !== routeId));
      if (selectedRoute?.id === routeId) {
        setSelectedRoute(null);
      }
      toast.success('Route deleted');
    } catch (error) {
      toast.error('Failed to delete route');
    }
  };

  const handleArchiveRoute = async (routeId) => {
    try {
      await api.archiveRoute(routeId);
      setRoutes(routes.filter(r => r.id !== routeId));
      if (selectedRoute?.id === routeId) {
        setSelectedRoute(null);
      }
      toast.success('Route archived');
    } catch (error) {
      toast.error('Failed to archive route');
    }
  };

  const handleUnarchiveRoute = async (routeId) => {
    try {
      await api.unarchiveRoute(routeId);
      setRoutes(routes.filter(r => r.id !== routeId));
      if (selectedRoute?.id === routeId) {
        setSelectedRoute(null);
      }
      toast.success('Route restored');
    } catch (error) {
      toast.error('Failed to restore route');
    }
  };

  const handleFixCoordinates = async (routeId) => {
    try {
      toast.info('Fixing stop coordinates...');
      const response = await api.fixRouteCoordinates(routeId);
      if (response.data.fixed_count > 0) {
        setSelectedRoute(response.data.route);
        setRoutes(routes.map(r => r.id === response.data.route.id ? response.data.route : r));
        fetchDirections(routeId);
        toast.success(`Fixed ${response.data.fixed_count} stop(s) with missing coordinates`);
      } else {
        toast.info('All stops already have coordinates');
      }
    } catch (error) {
      toast.error('Failed to fix coordinates');
    }
  };

  const handleDuplicateRoute = async () => {
    if (!selectedRoute) return;
    try {
      const today = new Date().toISOString().split('T')[0];
      const { data } = await api.duplicateRoute(selectedRoute.id, { new_date: today });
      setRoutes([data, ...routes]);
      setSelectedRoute(data);
      setRouteDirections(null);
      toast.success('Route duplicated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to duplicate route');
    }
  };

  // Generate a printable Route PDF (optionally with turn-by-turn worded directions).
  const handleDownloadRoutePdf = async (includeDirections = false) => {
    if (!selectedRoute) return;
    setGeneratingPdf(true);
    try {
      const { data } = await api.getRoutePDFData(selectedRoute.id, includeDirections);
      const { default: html2pdf } = await import('html2pdf.js');
      const html = buildRoutePrintHtml(data);

      // Render into a hidden container so html2pdf can capture it
      const container = document.createElement('div');
      container.style.position = 'fixed';
      container.style.left = '-9999px';
      container.style.top = '0';
      container.innerHTML = html;
      document.body.appendChild(container);

      const safeName = (data.route?.name || 'Route').replace(/[^a-z0-9-_ ]/gi, '').replace(/\s+/g, '_');
      await html2pdf().set({
        margin: 0.25,
        filename: `${safeName}_${data.route?.date || ''}.pdf`,
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
      }).from(container.firstElementChild).save();

      document.body.removeChild(container);
      toast.success('Route PDF downloaded');
      setShowPdfDialog(false);
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate Route PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!selectedRoute) return;
    const suggested = selectedRoute.template_name || selectedRoute.name || '';
    const name = window.prompt('Template name (drivers will pick it from the "From template" menu):', suggested);
    if (name == null) return;
    try {
      await api.saveRouteAsTemplate(selectedRoute.id, name.trim() || suggested);
      // Refresh routes list — the template is now excluded from the active list
      const updated = { ...selectedRoute, is_template: true, template_name: name.trim() || suggested };
      setSelectedRoute(updated);
      setRoutes(routes.filter(r => r.id !== selectedRoute.id));
      toast.success('Saved as template');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleUnmarkTemplate = async () => {
    if (!selectedRoute) return;
    try {
      await api.unmarkRouteTemplate(selectedRoute.id);
      const updated = { ...selectedRoute, is_template: false, template_name: null };
      setSelectedRoute(updated);
      toast.success('Removed template flag');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update');
    }
  };

  const handleEditRoute = () => {
    if (!selectedRoute) return;
    setEditingRoute({
      name: selectedRoute.name,
      date: selectedRoute.date,
      use_current_location: selectedRoute.use_current_location || false,
      start_address: selectedRoute.start_address || '',
      end_address: selectedRoute.end_address || '',
      return_to_start: selectedRoute.return_to_start || false,
      time_per_stop_minutes: selectedRoute.time_per_stop_minutes || 5,
      avoid_tolls: !!selectedRoute.avoid_tolls,
      avoid_highways: !!selectedRoute.avoid_highways,
      avoid_unpaved: !!selectedRoute.avoid_unpaved,
      scheduled_start_time: selectedRoute.scheduled_start_time || null
    });
    setShowEditRouteDialog(true);
  };

  const handleSaveEditedRoute = async () => {
    if (!editingRoute || !selectedRoute) return;
    
    try {
      setEditingRouteGeocoding(true);
      
      // Geocode start address if provided and not using current location
      let startLat = null, startLng = null;
      if (editingRoute.start_address && !editingRoute.use_current_location) {
        try {
          const geoRes = await api.geocodeAddress(editingRoute.start_address);
          startLat = geoRes.data.lat;
          startLng = geoRes.data.lng;
        } catch {
          toast.warning('Could not geocode start address');
        }
      }
      
      // Geocode end address if provided (only if not returning to start)
      let endLat = null, endLng = null;
      let endAddress = editingRoute.end_address;
      
      if (editingRoute.return_to_start) {
        endAddress = editingRoute.start_address || null;
        endLat = startLat;
        endLng = startLng;
      } else if (editingRoute.end_address) {
        try {
          const geoRes = await api.geocodeAddress(editingRoute.end_address);
          endLat = geoRes.data.lat;
          endLng = geoRes.data.lng;
        } catch {
          toast.warning('Could not geocode end address');
        }
      }
      
      const updates = {
        name: editingRoute.name,
        date: editingRoute.date,
        use_current_location: editingRoute.use_current_location,
        start_address: editingRoute.start_address || null,
        start_lat: startLat,
        start_lng: startLng,
        end_address: endAddress || null,
        end_lat: endLat,
        end_lng: endLng,
        return_to_start: editingRoute.return_to_start,
        time_per_stop_minutes: editingRoute.time_per_stop_minutes,
        avoid_tolls: !!editingRoute.avoid_tolls,
        avoid_highways: !!editingRoute.avoid_highways,
        avoid_unpaved: !!editingRoute.avoid_unpaved,
        scheduled_start_time: editingRoute.scheduled_start_time || null
      };
      
      const response = await api.updateRoute(selectedRoute.id, updates);
      setSelectedRoute(response.data);
      setRoutes(routes.map(r => r.id === response.data.id ? response.data : r));
      setShowEditRouteDialog(false);
      setEditingRoute(null);
      
      // Refresh directions to recalculate with new service time
      fetchDirections(selectedRoute.id);
      toast.success('Route updated');
    } catch (error) {
      toast.error('Failed to update route');
    } finally {
      setEditingRouteGeocoding(false);
    }
  };

  // Get the next incomplete stop
  const getNextStop = () => {
    if (!selectedRoute || !selectedRoute.stops?.length) return null;
    return selectedRoute.stops.find(s => s.status !== 'completed' && s.status !== 'skipped');
  };

  // Generate navigation URL for a single address - with auto-start where possible
  const generateNavigationUrl = (address, app = 'google', coords = null) => {
    if (!address) return null;
    
    const encodedAddress = encodeURIComponent(address);
    
    switch (app) {
      case 'google':
        // Use dir_action=navigate to auto-start turn-by-turn navigation
        if (coords?.lat && coords?.lng) {
          return `https://www.google.com/maps/dir/?api=1&destination=${coords.lat},${coords.lng}&travelmode=driving&dir_action=navigate`;
        }
        return `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}&travelmode=driving&dir_action=navigate`;
      case 'apple':
        if (coords?.lat && coords?.lng) {
          return `https://maps.apple.com/?daddr=${coords.lat},${coords.lng}&dirflg=d`;
        }
        return `https://maps.apple.com/?daddr=${encodedAddress}&dirflg=d`;
      case 'waze':
        // Waze navigate=yes already auto-starts
        if (coords?.lat && coords?.lng) {
          return `https://waze.com/ul?ll=${coords.lat},${coords.lng}&navigate=yes`;
        }
        return `https://waze.com/ul?q=${encodedAddress}&navigate=yes`;
      default:
        return `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}&travelmode=driving&dir_action=navigate`;
    }
  };

  // Save navigation preference
  const saveNavPreference = (app) => {
    localStorage.setItem('servio_nav_preference', app);
    setNavPreference(app);
    toast.success(`Default navigation set to ${app === 'google' ? 'Google Maps' : app === 'apple' ? 'Apple Maps' : 'Waze'}`);
  };
  
  // Toggle auto-navigate setting
  const toggleAutoNavigate = () => {
    const newValue = !autoNavigate;
    setAutoNavigate(newValue);
    localStorage.setItem('servio_auto_navigate', newValue.toString());
    toast.success(newValue ? 'Auto-navigate enabled' : 'Auto-navigate disabled');
  };

  // Navigate to specific stop - uses preference or shows picker if no preference set
  const handleNavigateToStop = (stop) => {
    if (navPreference) {
      // Use saved preference - pass coordinates if available for better accuracy
      const coords = stop.lat && stop.lng ? { lat: stop.lat, lng: stop.lng } : null;
      const url = generateNavigationUrl(stop.address, navPreference, coords);
      if (url) {
        if (selectedRoute.status === 'draft') {
          api.updateRoute(selectedRoute.id, { status: 'in_progress' });
          setSelectedRoute(prev => ({ ...prev, status: 'in_progress' }));
          setRoutes(routes.map(r => r.id === selectedRoute.id ? { ...r, status: 'in_progress' } : r));
        }
        window.open(url, '_blank');
      } else {
        toast.error('Invalid address');
      }
    } else {
      // No preference set - show picker and save choice
      setNavigatingStop(stop);
      setShowNavPicker(true);
    }
  };
  
  // Handle navigation app selection from picker (just saves preference, doesn't navigate)
  const handleNavAppSelect = (app) => {
    saveNavPreference(app);
    setShowNavPicker(false);
    setNavigatingStop(null);
    // Don't navigate - just save the preference. User will click Navigate again to actually go.
  };

  // Navigate to next incomplete stop
  const handleNavigateNext = () => {
    const nextStop = getNextStop();
    if (nextStop) {
      handleNavigateToStop(nextStop);
    } else {
      toast.info('All stops completed!');
    }
  };

  // Generate full route URL for Google Maps (multi-stop only works with Google)
  const generateFullRouteUrl = () => {
    if (!selectedRoute || !selectedRoute.stops.length) return null;
    
    const stops = selectedRoute.stops.filter(s => s.status !== 'completed' && s.status !== 'skipped');
    if (!stops.length) return null;
    
    // Build waypoints - Google Maps URL format
    let url = 'https://www.google.com/maps/dir/';
    
    // Add start location
    if (selectedRoute.use_current_location) {
      url += 'Current+Location/';
    } else if (selectedRoute.start_address) {
      url += encodeURIComponent(selectedRoute.start_address) + '/';
    }
    
    // Add stops (limit to 10 for Google Maps URL)
    const stopsToAdd = stops.slice(0, 10);
    stopsToAdd.forEach(stop => {
      url += encodeURIComponent(stop.address) + '/';
    });
    
    // Add end location - either return to start or custom end address
    if (selectedRoute.return_to_start) {
      if (selectedRoute.use_current_location) {
        url += 'Current+Location';
      } else if (selectedRoute.start_address) {
        url += encodeURIComponent(selectedRoute.start_address);
      }
    } else if (selectedRoute.end_address) {
      url += encodeURIComponent(selectedRoute.end_address);
    }
    
    return url;
  };

  const handleNavigateFullRoute = () => {
    const url = generateFullRouteUrl();
    if (url) {
      // Update route status to in_progress
      api.updateRoute(selectedRoute.id, { status: 'in_progress' });
      setSelectedRoute(prev => ({ ...prev, status: 'in_progress' }));
      setRoutes(routes.map(r => r.id === selectedRoute.id ? { ...r, status: 'in_progress' } : r));
      window.open(url, '_blank');
    } else {
      toast.error('No stops to navigate to');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500/20 text-green-400">Completed</Badge>;
      case 'in_progress':
        return <Badge className="bg-yellow-500/20 text-yellow-400">In Progress</Badge>;
      case 'skipped':
        return <Badge className="bg-slate-500/20 text-slate-400">Skipped</Badge>;
      case 'archived':
        return <Badge className="bg-purple-500/20 text-purple-400">Archived</Badge>;
      default:
        return <Badge className="bg-slate-500/20 text-slate-400">Draft</Badge>;
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'first':
        return <Badge className="bg-red-500/20 text-red-400 text-xs">FIRST</Badge>;
      case 'last':
        return <Badge className="bg-blue-500/20 text-blue-400 text-xs">LAST</Badge>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <Route className="h-5 w-5 text-teal-500" />
            Route Optimizer
          </h2>
          <p className="text-sm text-slate-400 mt-1">Plan and optimize your daily routes</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowTemplatePicker(true)} variant="outline" className="border-slate-300 dark:border-slate-600" data-testid="from-template-btn">
            <Star className="h-4 w-4 mr-2" />
            From template
          </Button>
          <Button onClick={() => {
            setSelectedRoute(null);
            setRouteDirections(null);
            setShowNewRouteDialog(true);
          }} className="btn-accent">
            <Plus className="h-4 w-4 mr-2" />
            New Route
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Routes List */}
        <div className="lg:col-span-1 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider">
              {showArchived ? 'Archived Routes' : 'Your Routes'}
            </h3>
            <Button
              variant="ghost"
              size="sm"
              className={`text-xs ${showArchived ? 'text-teal-400' : 'text-slate-500'} hover:text-slate-300`}
              onClick={() => {
                setShowArchived(!showArchived);
                setSelectedRoute(null);
              }}
            >
              <Archive className="h-3 w-3 mr-1" />
              {showArchived ? 'Show Active' : 'Show Archived'}
            </Button>
          </div>
          
          {routes.length === 0 ? (
            <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
              <CardContent className="py-8 text-center">
                <Route className="h-10 w-10 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No routes yet</p>
                <p className="text-sm text-slate-500 mt-1">Create your first route to get started</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-5 max-h-[600px] overflow-y-auto pr-2">
              {(() => {
                // Group routes into Today / Upcoming / History buckets.
                // Date strings are YYYY-MM-DD (no timezone) — compare as strings to avoid UTC drift.
                const today = new Date().toISOString().split('T')[0];
                const todayRoutes = [];
                const upcomingRoutes = [];
                const historyRoutes = [];
                routes.forEach(r => {
                  const d = (r.date || '').slice(0, 10);
                  if (d === today) todayRoutes.push(r);
                  else if (d > today) upcomingRoutes.push(r);
                  else historyRoutes.push(r);
                });
                // Sort upcoming asc, history desc
                upcomingRoutes.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
                historyRoutes.sort((a, b) => (b.date || '').localeCompare(a.date || ''));

                const renderCard = (route, isHero = false) => (
                  <Card
                    key={route.id}
                    className={
                      `cursor-pointer transition-all ` +
                      (isHero
                        ? `bg-gradient-to-br from-[hsl(226,100%,55%)]/12 to-[hsl(270,80%,55%)]/8 border-2 border-[hsl(226,100%,55%)]/40 hover:border-[hsl(226,100%,55%)] shadow-[0_8px_24px_-12px_hsl(226,100%,55%/0.4)] ${selectedRoute?.id === route.id ? 'ring-2 ring-[hsl(226,100%,55%)]/30' : ''}`
                        : `bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 hover:border-[hsl(226,100%,55%)]/40 ${selectedRoute?.id === route.id ? 'border-[hsl(226,100%,55%)] ring-1 ring-[hsl(226,100%,55%)]/20' : ''}`)
                    }
                    onClick={() => handleSelectRoute(route)}
                  >
                    <CardContent className={isHero ? 'p-5' : 'p-4'}>
                      {isHero && (
                        <div className="flex items-center gap-2 mb-2">
                          <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full rounded-full bg-[hsl(150,80%,55%)] opacity-70 animate-ping" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-[hsl(150,80%,55%)]" />
                          </span>
                          <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[hsl(226,100%,55%)] dark:text-[hsl(226,100%,68%)] font-bold">◢ TODAY</span>
                        </div>
                      )}
                      <div className="flex items-start justify-between">
                        <div className="min-w-0">
                          <h4 className={(isHero ? 'text-lg font-bold tracking-tight ' : 'font-medium ') + 'text-slate-900 dark:text-white truncate'}>{route.name}</h4>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{formatDate(route.date)}</p>
                        </div>
                        {getStatusBadge(route.status)}
                      </div>
                      <div className={'flex items-center gap-4 mt-3 ' + (isHero ? 'text-sm' : 'text-xs') + ' text-slate-500 dark:text-slate-400'}>
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {route.stops?.length || 0} stops
                        </span>
                        {route.scheduled_start_time && isScheduledTimeInFuture(route.scheduled_start_time) && (
                          <span className="flex items-center gap-1 text-[hsl(226,100%,55%)] dark:text-[hsl(226,100%,68%)]">
                            <Clock className="h-3 w-3" />
                            Starts {formatTime12h(route.scheduled_start_time)}
                          </span>
                        )}
                        {(!route.scheduled_start_time || !isScheduledTimeInFuture(route.scheduled_start_time)) && (route.total_distance > 0 || route.total_duration > 0) && (
                          <span className="flex items-center gap-1">
                            {route.total_distance > 0 && `${route.total_distance} mi`}
                            {route.total_duration > 0 && (
                              <>
                                {route.total_distance > 0 && ' • '}
                                <Clock className="h-3 w-3" />
                                {formatDuration(route.total_duration)}
                              </>
                            )}
                          </span>
                        )}
                      </div>
                      {isHero && route.stops?.length > 0 && (() => {
                        const done = (route.stops || []).filter(s => s.status === 'completed' || s.status === 'skipped').length;
                        const pct = Math.round((done / route.stops.length) * 100);
                        return (
                          <div className="mt-4">
                            <div className="flex justify-between text-[11px] font-mono text-slate-600 dark:text-slate-300 mb-1">
                              <span>{done} of {route.stops.length} done</span>
                              <span>{pct}%</span>
                            </div>
                            <div className="h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                              <div className="h-full bg-[hsl(226,100%,55%)] transition-all" style={{ width: pct + '%' }} />
                            </div>
                          </div>
                        );
                      })()}
                    </CardContent>
                  </Card>
                );

                return (
                  <>
                    {todayRoutes.length > 0 && (
                      <div className="space-y-2">
                        {todayRoutes.map(r => renderCard(r, true))}
                      </div>
                    )}
                    {upcomingRoutes.length > 0 && (
                      <div className="space-y-2">
                        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500 dark:text-slate-400 px-1">Upcoming · {upcomingRoutes.length}</div>
                        {upcomingRoutes.map(r => renderCard(r, false))}
                      </div>
                    )}
                    {historyRoutes.length > 0 && (
                      <div className="space-y-2">
                        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500 dark:text-slate-400 px-1">History · {historyRoutes.length}</div>
                        {historyRoutes.map(r => renderCard(r, false))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {/* Route Details */}
        <div className="lg:col-span-2">
          {selectedRoute ? (
            <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-3">
                {/* Mobile-optimized header */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-base sm:text-lg text-slate-900 dark:text-white truncate">{selectedRoute.name}</CardTitle>
                      {getStatusBadge(selectedRoute.status)}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                      onClick={handleEditRoute}
                      data-testid="edit-route-btn"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-slate-400 hover:text-slate-300 hover:bg-slate-500/10"
                          data-testid="route-more-options"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                        <DropdownMenuItem
                          onClick={handleDuplicateRoute}
                          className="text-slate-200 hover:text-white hover:bg-slate-700 cursor-pointer"
                          data-testid="duplicate-route-btn"
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          Duplicate Route
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => setShowPdfDialog(true)}
                          className="text-slate-200 hover:text-white hover:bg-slate-700 cursor-pointer"
                          data-testid="download-route-pdf-btn"
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          Download as PDF
                        </DropdownMenuItem>
                        {selectedRoute.is_template ? (
                          <DropdownMenuItem
                            onClick={handleUnmarkTemplate}
                            className="text-slate-300 hover:text-white hover:bg-slate-700 cursor-pointer"
                            data-testid="unmark-template-btn"
                          >
                            <Star className="h-4 w-4 mr-2" />
                            Remove template flag
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={handleSaveAsTemplate}
                            className="text-slate-300 hover:text-white hover:bg-slate-700 cursor-pointer"
                            data-testid="save-as-template-btn"
                          >
                            <Star className="h-4 w-4 mr-2" />
                            Save as template
                          </DropdownMenuItem>
                        )}
                        {selectedRoute.status === 'completed' && (
                          <DropdownMenuItem 
                            onClick={() => handleArchiveRoute(selectedRoute.id)}
                            className="text-slate-300 hover:text-white hover:bg-slate-700 cursor-pointer"
                          >
                            <Archive className="h-4 w-4 mr-2" />
                            Archive Route
                          </DropdownMenuItem>
                        )}
                        {selectedRoute.status === 'archived' && (
                          <DropdownMenuItem 
                            onClick={() => handleUnarchiveRoute(selectedRoute.id)}
                            className="text-teal-400 hover:text-teal-300 hover:bg-teal-500/10 cursor-pointer"
                          >
                            <RotateCcw className="h-4 w-4 mr-2" />
                            Restore Route
                          </DropdownMenuItem>
                        )}
                        {selectedRoute.stops?.some(s => !s.lat || !s.lng) && (
                          <DropdownMenuItem 
                            onClick={() => handleFixCoordinates(selectedRoute.id)}
                            className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10 cursor-pointer"
                          >
                            <Wrench className="h-4 w-4 mr-2" />
                            Fix Missing Coordinates
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator className="bg-slate-700" />
                        <DropdownMenuItem 
                          onClick={() => handleDeleteRoute(selectedRoute.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10 cursor-pointer"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete Route
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                
                {/* Stats row - compact for mobile */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs sm:text-sm text-slate-400">
                  <span>{formatDate(selectedRoute.date)}</span>
                  <span>•</span>
                  <span>{selectedRoute.stops?.length || 0} stops</span>
                  {selectedRoute.scheduled_start_time && isScheduledTimeInFuture(selectedRoute.scheduled_start_time) && (
                    <>
                      <span>•</span>
                      <span className="flex items-center gap-1 text-teal-500 font-medium">
                        <Clock className="h-3 w-3" />
                        Scheduled: {formatTime12h(selectedRoute.scheduled_start_time)}
                      </span>
                    </>
                  )}
                  {loadingDirections && <Loader2 className="h-3 w-3 animate-spin" />}
                </div>
                
                {/* Distance/Time Stats - Initial vs Remaining */}
                {(routeDirections || selectedRoute.total_distance > 0 || selectedRoute.initial_total_distance > 0) && (
                  <div className="mt-2 p-2 bg-slate-100 dark:bg-slate-900/50 rounded-lg">
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      {/* Initial Stats - use initial_total_distance/duration which are stored permanently */}
                      <div>
                        <p className="text-slate-500 mb-1">Initial Route</p>
                        <p className="text-slate-700 dark:text-slate-300 font-medium">
                          {selectedRoute.initial_total_distance || routeDirections?.initial_distance_miles || selectedRoute.total_distance || 0} mi
                          {(selectedRoute.initial_total_duration || routeDirections?.initial_duration_minutes || routeDirections?.duration_minutes) && (
                            <span className="text-slate-500 font-normal"> • {formatDuration(selectedRoute.initial_total_duration || routeDirections?.initial_duration_minutes || routeDirections?.duration_minutes)}</span>
                          )}
                        </p>
                        {routeDirections?.service_time_minutes > 0 && (
                          <p className="text-[10px] text-slate-500 mt-0.5">
                            Drive: {formatDuration(routeDirections.driving_minutes)} + Stops: {formatDuration(routeDirections.service_time_minutes)}
                          </p>
                        )}
                      </div>
                      {/* Remaining Stats */}
                      <div>
                        <p className="text-teal-500 mb-1">Remaining</p>
                        {selectedRoute.stops?.some(s => s.status !== 'pending') ? (
                          <>
                            <p className="text-teal-400 font-medium">
                              {selectedRoute.remaining_distance ?? selectedRoute.total_distance ?? 0} mi
                              {selectedRoute.remaining_duration !== undefined && selectedRoute.remaining_duration !== null && (
                                <span className="text-teal-500/70 font-normal"> • {formatDuration(selectedRoute.remaining_duration)}</span>
                              )}
                            </p>
                            {(() => {
                              const pendingStops = selectedRoute.stops?.filter(s => s.status === 'pending') || [];
                              const defaultTimePerStop = selectedRoute.time_per_stop_minutes || 5;
                              // Sum up service times, using per-stop override if set, otherwise route default
                              const stopTime = pendingStops.reduce((total, stop) => {
                                return total + (stop.service_time_minutes || defaultTimePerStop);
                              }, 0);
                              const driveTime = (selectedRoute.remaining_duration || 0) - stopTime;
                              if (driveTime > 0 || stopTime > 0) {
                                return (
                                  <p className="text-[10px] text-teal-500/70 mt-0.5">
                                    Drive: {formatDuration(Math.max(0, driveTime))} + Stops: {formatDuration(stopTime)}
                                  </p>
                                );
                              }
                              return null;
                            })()}
                          </>
                        ) : (
                          <p className="text-slate-500">Not started</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Start/End Info - compact */}
                <div className="mt-3 p-2 bg-slate-50 dark:bg-slate-900/50 rounded-lg text-xs sm:text-sm space-y-1">
                  <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0"></div>
                    <span className="text-slate-500">Start:</span>
                    <span className="truncate">{selectedRoute.use_current_location ? 'Current Location' : (selectedRoute.start_address || 'Not set')}</span>
                  </div>
                  {selectedRoute.return_to_start ? (
                    <div className="flex items-center gap-2 text-teal-400">
                      <RotateCcw className="h-3 w-3 flex-shrink-0" />
                      <span>Return to Start</span>
                    </div>
                  ) : selectedRoute.end_address && (
                    <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0"></div>
                      <span className="text-slate-500">End:</span>
                      <span className="truncate">{selectedRoute.end_address}</span>
                    </div>
                  )}
                </div>
                
                {/* Route Map */}
                {selectedRoute.stops?.length > 0 && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-medium text-slate-400">Route Map</h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-slate-400 hover:text-slate-900 dark:text-white h-6 px-2"
                        onClick={() => setShowMap(!showMap)}
                      >
                        {showMap ? 'Hide' : 'Show'}
                      </Button>
                    </div>
                    {showMap && (
                      <div className="h-[250px] rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 relative z-0">
                        <MapContainer
                          center={[
                            selectedRoute.stops[0]?.lat || 33.4484,
                            selectedRoute.stops[0]?.lng || -101.8466
                          ]}
                          zoom={11}
                          style={{ height: '100%', width: '100%', zIndex: 0 }}
                          scrollWheelZoom={false}
                        >
                          <TileLayer
                            url={`https://api.mapbox.com/styles/v1/mapbox/dark-v11/tiles/512/{z}/{x}/{y}@2x?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`}
                            attribution='&copy; <a href="https://www.mapbox.com/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                            tileSize={512}
                            zoomOffset={-1}
                          />
                          
                          {/* Map controller to handle flying to focused stop */}
                          <MapFocusController 
                            focusedStopId={focusedStopId} 
                            stops={selectedRoute.stops} 
                          />
                          
                          {/* Start marker */}
                          {selectedRoute.start_lat && selectedRoute.start_lng && (
                            <Marker
                              position={[selectedRoute.start_lat, selectedRoute.start_lng]}
                              icon={createStartEndIcon(true)}
                            >
                              <Popup>
                                <strong>Start:</strong><br />
                                {selectedRoute.start_address || 'Starting Point'}
                              </Popup>
                            </Marker>
                          )}
                          
                          {/* Stop markers */}
                          {selectedRoute.stops.map((stop, index) => {
                            const hasViolation = timeViolations.some(v => v.stop_id === stop.id);
                            const hasTimeConstraint = stop.time_constraint_type && stop.time_constraint_type !== 'none';
                            return stop.lat && stop.lng && (
                              <Marker
                                key={stop.id}
                                position={[stop.lat, stop.lng]}
                                icon={createNumberedIcon(index + 1, stop.status, hasViolation, hasTimeConstraint)}
                              >
                                <Popup>
                                  <strong>Stop {index + 1}</strong><br />
                                  {stop.address}<br />
                                  {stop.defendant_name && <span>Defendant: {stop.defendant_name}<br /></span>}
                                  {hasTimeConstraint && (
                                    <span style={{color: '#f59e0b'}}>
                                      ⏰ {stop.time_constraint_type === 'arrive_before' ? `Before ${stop.time_constraint_time}` : 
                                          stop.time_constraint_type === 'arrive_after' ? `After ${stop.time_constraint_time}` :
                                          `Between ${stop.time_window_start} - ${stop.time_window_end}`}
                                      <br />
                                    </span>
                                  )}
                                  {stop.status !== 'pending' && <em>({stop.status})</em>}
                                  {hasViolation && <span style={{color: 'red'}}><br />⚠️ Time constraint conflict</span>}
                                </Popup>
                              </Marker>
                            );
                          })}
                          
                          {/* End marker (if different from start) */}
                          {selectedRoute.end_lat && selectedRoute.end_lng && !selectedRoute.return_to_start && (
                            <Marker
                              position={[selectedRoute.end_lat, selectedRoute.end_lng]}
                              icon={createStartEndIcon(false)}
                            >
                              <Popup>
                                <strong>End:</strong><br />
                                {selectedRoute.end_address || 'Ending Point'}
                              </Popup>
                            </Marker>
                          )}
                          
                          {/* Route line - use directions API or stored geometry as fallback */}
                          {(routeDirections?.geometry?.coordinates || selectedRoute.route_geometry?.coordinates) && (
                            <Polyline
                              positions={(routeDirections?.geometry?.coordinates || selectedRoute.route_geometry?.coordinates).map(c => [c[1], c[0]])}
                              color="#14b8a6"
                              weight={4}
                              opacity={0.8}
                            />
                          )}
                          
                          {/* Fit bounds to all markers */}
                          <FitBounds bounds={[
                            ...(selectedRoute.start_lat && selectedRoute.start_lng ? [[selectedRoute.start_lat, selectedRoute.start_lng]] : []),
                            ...selectedRoute.stops.filter(s => s.lat && s.lng).map(s => [s.lat, s.lng]),
                            ...(selectedRoute.end_lat && selectedRoute.end_lng && !selectedRoute.return_to_start ? [[selectedRoute.end_lat, selectedRoute.end_lng]] : [])
                          ]} />
                        </MapContainer>
                      </div>
                    )}
                  </div>
                )}
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Action Buttons - Simplified */}
                <div className="flex flex-wrap gap-2">
                  {/* Primary Action: Add Stop */}
                  <Button onClick={() => setShowAddStopDialog(true)} variant="outline" size="sm" className="border-slate-300 dark:border-slate-600">
                    <Plus className="h-4 w-4 mr-1" />
                    Add Stop
                  </Button>
                  
                  {/* Primary Action: Optimize */}
                  <Button 
                    onClick={handleOptimize} 
                    variant="outline" 
                    size="sm" 
                    className="border-teal-500/50 text-teal-400 hover:bg-teal-500/10"
                    disabled={optimizing || (selectedRoute.stops?.length || 0) < 2}
                  >
                    {optimizing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Route className="h-4 w-4 mr-1" />}
                    Optimize
                  </Button>
                  
                  {/* Start Route - only show for OPTIMIZED but not started routes */}
                  {selectedRoute.status === 'draft' && 
                   selectedRoute.stops?.length >= 1 && 
                   selectedRoute.total_distance > 0 &&
                   selectedRoute.stops?.some(s => s.estimated_arrival) && (
                    <Button 
                      onClick={handleStartRoute} 
                      size="sm" 
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid="start-route-btn"
                    >
                      <Play className="h-4 w-4 mr-1" />
                      Start Route
                    </Button>
                  )}

                  {/* Analytics - Show prominently when route has completed stops */}
                  {selectedRoute.stops?.some(s => ['completed', 'successful', 'unsuccessful', 'skipped'].includes(s.status)) && (
                    <Button
                      onClick={() => {
                        setAnalyticsRouteId(selectedRoute.id);
                        setShowAnalyticsDialog(true);
                      }}
                      variant="outline"
                      size="sm"
                      className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                      title="View route analytics"
                      data-testid="route-analytics-btn"
                    >
                      <BarChart3 className="h-4 w-4 mr-1" />
                      Analytics
                    </Button>
                  )}
                  
                  {/* More Actions Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="border-slate-300 dark:border-slate-600">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                        </svg>
                        <span className="ml-1">More</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuLabel>Route Actions</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      
                      {/* Add Stop Mid-Route - for active routes */}
                      {(selectedRoute.status === 'active' || selectedRoute.status === 'optimized') && (
                        <DropdownMenuItem onClick={() => setShowAddStopMidRouteDialog(true)}>
                          <Plus className="h-4 w-4 mr-2 text-green-400" />
                          Add Stop (Mid-Route)
                        </DropdownMenuItem>
                      )}
                      
                      <DropdownMenuItem onClick={() => setShowImportDialog(true)}>
                        <svg className="h-4 w-4 mr-2 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        Import CSV
                      </DropdownMenuItem>
                      
                      <DropdownMenuItem 
                        onClick={handleReverseRoute}
                        disabled={(selectedRoute.stops?.length || 0) < 2}
                      >
                        <ArrowLeftRight className="h-4 w-4 mr-2" />
                        Reverse Order
                      </DropdownMenuItem>
                      
                      <DropdownMenuItem 
                        onClick={handleResetRoute}
                        disabled={
                          !selectedRoute.stops?.length || 
                          selectedRoute.stops.some(s => !['completed', 'successful', 'unsuccessful', 'skipped'].includes(s.status))
                        }
                      >
                        <RotateCcw className="h-4 w-4 mr-2 text-amber-400" />
                        Reset Route
                      </DropdownMenuItem>
                      
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel>Export & Share</DropdownMenuLabel>
                      
                      <DropdownMenuItem 
                        onClick={handleExportPdf}
                        disabled={!selectedRoute.stops?.length}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Export PDF
                      </DropdownMenuItem>
                      
                      <DropdownMenuItem 
                        onClick={handleShareRoute}
                        disabled={!selectedRoute.stops?.length}
                      >
                        <Share2 className="h-4 w-4 mr-2 text-blue-400" />
                        Share Live Link
                      </DropdownMenuItem>
                      
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel>Navigation</DropdownMenuLabel>
                      
                      <DropdownMenuItem onClick={() => { saveNavPreference('google'); }}>
                        {navPreference === 'google' && <Check className="h-4 w-4 mr-2" />}
                        <span className={navPreference !== 'google' ? 'ml-6' : ''}>Google Maps</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { saveNavPreference('apple'); }}>
                        {navPreference === 'apple' && <Check className="h-4 w-4 mr-2" />}
                        <span className={navPreference !== 'apple' ? 'ml-6' : ''}>Apple Maps</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { saveNavPreference('waze'); }}>
                        {navPreference === 'waze' && <Check className="h-4 w-4 mr-2" />}
                        <span className={navPreference !== 'waze' ? 'ml-6' : ''}>Waze</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={toggleAutoNavigate}>
                        {autoNavigate && <Check className="h-4 w-4 mr-2" />}
                        <span className={!autoNavigate ? 'ml-6' : ''}>Auto-Navigate</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Stops List */}
                <div className="space-y-2">
                  <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wide">Stops <span className="text-slate-600">(drag to reorder)</span></h4>
                  
                  {/* Starting Location Card */}
                  {selectedRoute.stops?.length > 0 && (selectedRoute.start_address || selectedRoute.use_current_location) && (
                    <div className="mb-3 p-3 rounded-lg border border-dashed border-green-500/50 bg-green-500/5">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center bg-green-500/20">
                          <LocateFixed className="h-3 w-3 text-green-400" />
                        </div>
                        <div>
                          <p className="text-xs text-green-400 font-medium">Starting From</p>
                          <p className="text-sm text-white truncate max-w-[280px]">
                            {selectedRoute.use_current_location ? 'Current Location' : selectedRoute.start_address}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {!selectedRoute.stops?.length ? (
                    <div className="text-center py-6 border border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
                      <MapPin className="h-6 w-6 text-slate-600 mx-auto mb-2" />
                      <p className="text-sm text-slate-400">No stops added yet</p>
                      <Button 
                        variant="link" 
                        className="text-teal-400 text-sm"
                        onClick={() => setShowAddStopDialog(true)}
                      >
                        Add your first stop
                      </Button>
                    </div>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext items={selectedRoute.stops.map(s => s.id)} strategy={verticalListSortingStrategy}>
                        <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                          {selectedRoute.stops.map((stop, index) => (
                            <SortableStopItem
                              key={stop.id}
                              stop={stop}
                              index={index}
                              onNavigate={handleNavigateToStop}
                              onComplete={handleCompleteStop}
                              onEdit={handleEditStop}
                              onDelete={handleDeleteStop}
                              onFocusOnMap={(stopId) => {
                                console.log('Focus on map triggered for stop:', stopId);
                                setFocusedStopId(stopId);
                                // Clear focus after 2 seconds so clicking same stop again works
                                setTimeout(() => setFocusedStopId(null), 2000);
                              }}
                              violation={timeViolations.find(v => v.stop_id === stop.id)}
                              autoNavigate={autoNavigate}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  )}
                  
                  {/* Ending At - Return to Start or End Address */}
                  {selectedRoute.stops?.length > 0 && (selectedRoute.return_to_start || selectedRoute.end_address) && (
                    <div className="mt-3 p-3 rounded-lg border border-dashed border-teal-500/50 bg-teal-500/5">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center bg-teal-500/20 flex-shrink-0">
                            <Flag className="h-3 w-3 text-teal-400" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-teal-400 font-medium">Ending At</p>
                            </div>
                            <p className="text-sm text-white break-words">
                              {selectedRoute.return_to_start 
                                ? (selectedRoute.use_current_location ? 'Starting Location' : selectedRoute.start_address)
                                : selectedRoute.end_address}
                            </p>
                            {/* Calculate and show ETA based on last stop or current time if all done */}
                            {(() => {
                              const pendingStops = (selectedRoute.stops || []).filter(s => s.status === 'pending');
                              const allDone = pendingStops.length === 0;
                              const endDuration = selectedRoute.end_duration || 0;
                              
                              if (allDone && endDuration > 0) {
                                // All stops done - calculate from current time
                                const now = new Date();
                                const totalMinutes = now.getHours() * 60 + now.getMinutes() + Math.round(endDuration);
                                const etaHour = Math.floor(totalMinutes / 60) % 24;
                                const etaMin = totalMinutes % 60;
                                const etaStr = `${etaHour.toString().padStart(2, '0')}:${etaMin.toString().padStart(2, '0')}`;
                                return (
                                  <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded text-[10px] font-medium bg-teal-500/20 text-teal-400">
                                    <Clock className="h-2.5 w-2.5" />
                                    ETA {formatTime(etaStr)}
                                  </span>
                                );
                              } else if (!allDone) {
                                // Still have pending stops - calculate from last pending stop's ETA
                                const lastPendingStop = [...(selectedRoute.stops || [])].reverse().find(s => s.status === 'pending');
                                if (lastPendingStop?.estimated_arrival && endDuration > 0) {
                                  const [h, m] = lastPendingStop.estimated_arrival.split(':').map(Number);
                                  // Use the stop's custom service time if set, otherwise route default
                                  const stopServiceTime = lastPendingStop.service_time_minutes || selectedRoute.time_per_stop_minutes || 5;
                                  const totalMinutes = h * 60 + m + stopServiceTime + Math.round(endDuration);
                                  const etaHour = Math.floor(totalMinutes / 60) % 24;
                                  const etaMin = totalMinutes % 60;
                                  const etaStr = `${etaHour.toString().padStart(2, '0')}:${etaMin.toString().padStart(2, '0')}`;
                                  return (
                                    <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded text-[10px] font-medium bg-teal-500/20 text-teal-400">
                                      <Clock className="h-2.5 w-2.5" />
                                      ETA {formatTime(etaStr)}
                                    </span>
                                  );
                                }
                              }
                              return null;
                            })()}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-teal-400 hover:bg-teal-500/20 flex-shrink-0"
                          onClick={() => {
                            const address = selectedRoute.return_to_start 
                              ? selectedRoute.start_address 
                              : selectedRoute.end_address;
                            if (address) {
                              handleNavigateToStop({ address, id: 'final-destination' });
                            } else {
                              toast.info('Using current location - no navigation needed');
                            }
                          }}
                          title="Navigate to final destination"
                        >
                          <Navigation className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
              <CardContent className="py-12 px-6">
                <div className="max-w-md mx-auto text-center">
                  <div className="h-16 w-16 rounded-2xl bg-[hsl(226,100%,55%)]/10 border border-[hsl(226,100%,55%)]/20 flex items-center justify-center mx-auto mb-4">
                    <Route className="h-7 w-7 text-[hsl(226,100%,55%)]" />
                  </div>
                  <h3 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white">No route selected</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Pick one from the list or start a fresh route with any of these:</p>

                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <button
                      onClick={() => {
                        setSelectedRoute(null);
                        setRouteDirections(null);
                        setShowNewRouteDialog(true);
                      }}
                      data-testid="empty-state-new-route"
                      className="rounded-xl border border-slate-200 dark:border-slate-700 hover:border-[hsl(226,100%,55%)] hover:bg-slate-50 dark:hover:bg-slate-900/40 p-4 text-left transition-colors group"
                    >
                      <Plus className="h-5 w-5 text-[hsl(226,100%,55%)] mb-2" />
                      <div className="font-semibold text-sm text-slate-900 dark:text-white">Create from scratch</div>
                      <div className="text-xs text-slate-500 mt-0.5">Blank route, add stops one-by-one.</div>
                    </button>

                    <button
                      onClick={() => setShowTemplatePicker(true)}
                      data-testid="empty-state-from-template"
                      className="rounded-xl border border-slate-200 dark:border-slate-700 hover:border-amber-400 hover:bg-amber-50/40 dark:hover:bg-amber-950/20 p-4 text-left transition-colors group"
                    >
                      <Star className="h-5 w-5 text-amber-400 mb-2" />
                      <div className="font-semibold text-sm text-slate-900 dark:text-white">From template</div>
                      <div className="text-xs text-slate-500 mt-0.5">Reuse a saved route (e.g., Tuesday Dallas).</div>
                    </button>

                    <button
                      onClick={() => routes[0] && setSelectedRoute(routes[0])}
                      disabled={!routes[0]}
                      data-testid="empty-state-last-route"
                      className="rounded-xl border border-slate-200 dark:border-slate-700 hover:border-cyan-400 hover:bg-cyan-50/40 dark:hover:bg-cyan-950/20 p-4 text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed group"
                    >
                      <Copy className="h-5 w-5 text-cyan-500 mb-2" />
                      <div className="font-semibold text-sm text-slate-900 dark:text-white">Open most recent</div>
                      <div className="text-xs text-slate-500 mt-0.5 truncate">{routes[0] ? routes[0].name : 'No previous routes.'}</div>
                    </button>
                  </div>

                  <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 text-xs text-slate-600 dark:text-slate-400">
                    <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                    <span>Tip: optimizing a 10-stop route saves ~23 min on average.</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* New Route Dialog */}
      <Dialog open={showNewRouteDialog} onOpenChange={setShowNewRouteDialog}>
        <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 z-[100]">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white">Create New Route</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-700 dark:text-slate-300">Route Name</Label>
              <Input
                placeholder="e.g., Tuesday Downtown Route"
                value={newRoute.name}
                onChange={(e) => setNewRoute({ ...newRoute, name: e.target.value })}
                className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 mt-1"
              />
            </div>
            
            <div>
              <Label className="text-slate-700 dark:text-slate-300">Date</Label>
              <Input
                type="date"
                value={newRoute.date}
                onChange={(e) => setNewRoute({ ...newRoute, date: e.target.value })}
                className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 mt-1"
              />
            </div>
            
            <div>
              <Label className="text-slate-700 dark:text-slate-300">Starting Point</Label>
              <div className="space-y-2 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={newRoute.use_current_location}
                    onChange={() => {
                      setNewRoute({ ...newRoute, use_current_location: true });
                      setStartAddressVerified(false);
                      setShowStartSuggestions(false);
                    }}
                    className="text-teal-500"
                  />
                  <LocateFixed className="h-4 w-4 text-teal-500" />
                  <span className="text-slate-700 dark:text-slate-300">Use my current location</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={!newRoute.use_current_location}
                    onChange={() => setNewRoute({ ...newRoute, use_current_location: false })}
                    className="text-teal-500"
                  />
                  <MapPin className="h-4 w-4 text-slate-400" />
                  <span className="text-slate-700 dark:text-slate-300">Enter starting address</span>
                </label>
                {!newRoute.use_current_location && (
                  <div className="relative">
                    <div className="relative">
                      <Input
                        placeholder="Start typing an address..."
                        value={newRoute.start_address}
                        onChange={(e) => handleStartAddressChange(e.target.value, true)}
                        onFocus={() => newRoute.start_address.length >= 3 && setShowStartSuggestions(true)}
                        className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 mt-2"
                      />
                      {searchingStartAddress && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                        </div>
                      )}
                    </div>
                    
                    {showStartSuggestions && startAddressSuggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {startAddressSuggestions.map((suggestion, index) => (
                          <button
                            key={index}
                            type="button"
                            className="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-start gap-2 border-b border-slate-200 dark:border-slate-700 last:border-0"
                            onClick={() => handleSelectStartSuggestion(suggestion, true)}
                          >
                            <MapPin className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" />
                            <span>{suggestion.address}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {startAddressVerified && newRoute.start_address && (
                      <p className="text-xs text-teal-500 mt-1 flex items-center gap-1">
                        <Check className="h-3 w-3" /> Address verified
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-slate-700 dark:text-slate-300">Return to Start Point</Label>
                <Switch
                  checked={newRoute.return_to_start}
                  onCheckedChange={(checked) => {
                    setNewRoute({ ...newRoute, return_to_start: checked, end_address: '' });
                    setEndAddressVerified(false);
                    setShowEndSuggestions(false);
                  }}
                />
              </div>
              <p className="text-xs text-slate-500">When enabled, your route will end where it started</p>
            </div>
            
            {!newRoute.return_to_start && (
              <div className="relative">
                <Label className="text-slate-700 dark:text-slate-300">Ending Address (Optional)</Label>
                <div className="relative mt-1">
                  <Input
                    placeholder="Start typing an address..."
                    value={newRoute.end_address}
                    onChange={(e) => handleEndAddressChange(e.target.value, true)}
                    onFocus={() => newRoute.end_address.length >= 3 && setShowEndSuggestions(true)}
                    className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600"
                  />
                  {searchingEndAddress && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                    </div>
                  )}
                </div>
                
                {showEndSuggestions && endAddressSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {endAddressSuggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-start gap-2 border-b border-slate-200 dark:border-slate-700 last:border-0"
                        onClick={() => handleSelectEndSuggestion(suggestion, true)}
                      >
                        <MapPin className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" />
                        <span>{suggestion.address}</span>
                      </button>
                    ))}
                  </div>
                )}
                
                {endAddressVerified && newRoute.end_address && (
                  <p className="text-xs text-teal-500 mt-1 flex items-center gap-1">
                    <Check className="h-3 w-3" /> Address verified
                  </p>
                )}
                <p className="text-xs text-slate-500 mt-1">Leave blank to end at your last stop</p>
              </div>
            )}
            
            <div>
              <Label className="text-slate-700 dark:text-slate-300">Time Per Stop (minutes)</Label>
              <div className="flex items-center gap-3 mt-2">
                <Input
                  type="number"
                  min="0"
                  max="60"
                  value={newRoute.time_per_stop_minutes}
                  onChange={(e) => setNewRoute({ ...newRoute, time_per_stop_minutes: parseInt(e.target.value) || 0 })}
                  className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 w-24"
                />
                <span className="text-sm text-slate-400">minutes at each stop</span>
              </div>
              <p className="text-xs text-slate-500 mt-1">This time is added to the total route duration for service at each stop</p>
            </div>

            {/* Routing preferences */}
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40 p-3 space-y-2">
              <Label className="text-slate-700 dark:text-slate-300 text-sm">Routing preferences</Label>
              <p className="text-xs text-slate-500 -mt-1">Best-effort — if no alternative exists, the road is used anyway.</p>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700 dark:text-slate-300">
                <input type="checkbox" checked={newRoute.avoid_tolls}
                       onChange={(e) => setNewRoute({ ...newRoute, avoid_tolls: e.target.checked })}
                       data-testid="new-route-avoid-tolls"
                       className="h-4 w-4 accent-[hsl(226,100%,55%)]" />
                Avoid toll roads
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700 dark:text-slate-300">
                <input type="checkbox" checked={newRoute.avoid_highways}
                       onChange={(e) => setNewRoute({ ...newRoute, avoid_highways: e.target.checked })}
                       data-testid="new-route-avoid-highways"
                       className="h-4 w-4 accent-[hsl(226,100%,55%)]" />
                Avoid highways / motorways
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700 dark:text-slate-300">
                <input type="checkbox" checked={newRoute.avoid_unpaved}
                       onChange={(e) => setNewRoute({ ...newRoute, avoid_unpaved: e.target.checked })}
                       data-testid="new-route-avoid-unpaved"
                       className="h-4 w-4 accent-[hsl(226,100%,55%)]" />
                Avoid unpaved / dirt roads
              </label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewRouteDialog(false)} className="border-slate-300 dark:border-slate-600">
              Cancel
            </Button>
            <Button onClick={handleCreateRoute} className="btn-accent" disabled={geocoding}>
              {geocoding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Create Route
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Stop Dialog */}
      <Dialog open={showAddStopDialog} onOpenChange={(open) => {
        setShowAddStopDialog(open);
        if (!open) {
          setAddressSuggestions([]);
          setShowSuggestions(false);
          setSelectedAddressCoords({ lat: null, lng: null });
        }
      }}>
        <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 z-[100]">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white">Add Stop</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="relative">
              <Label className="text-slate-700 dark:text-slate-300">Address *</Label>
              <div className="relative mt-1">
                <Input
                  ref={addressInputRef}
                  placeholder="Start typing an address..."
                  value={newStop.address}
                  onChange={(e) => handleAddressInputChange(e.target.value)}
                  onFocus={() => newStop.address.length >= 3 && setShowSuggestions(true)}
                  className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600"
                />
                {searchingAddress && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                  </div>
                )}
              </div>
              
              {/* Address Suggestions Dropdown */}
              {showSuggestions && addressSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {addressSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      className="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-start gap-2 border-b border-slate-200 dark:border-slate-700 last:border-0"
                      onClick={() => handleSelectSuggestion(suggestion)}
                    >
                      <MapPin className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" />
                      <span>{suggestion.address}</span>
                    </button>
                  ))}
                </div>
              )}
              
              {selectedAddressCoords.lat && (
                <p className="text-xs text-teal-500 mt-1 flex items-center gap-1">
                  <Check className="h-3 w-3" /> Address verified
                </p>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700 dark:text-slate-300">Defendant Name</Label>
                <Input
                  placeholder="e.g., John Smith"
                  value={newStop.defendant_name}
                  onChange={(e) => setNewStop({ ...newStop, defendant_name: e.target.value })}
                  className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-700 dark:text-slate-300">Case Number</Label>
                <Input
                  placeholder="e.g., CV-2026-1234"
                  value={newStop.case_number}
                  onChange={(e) => setNewStop({ ...newStop, case_number: e.target.value })}
                  className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-slate-700 dark:text-slate-300">Notes</Label>
              <Textarea
                placeholder="e.g., Gate code: 1234, Ring doorbell twice"
                value={newStop.notes}
                onChange={(e) => setNewStop({ ...newStop, notes: e.target.value })}
                className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 mt-1"
                rows={2}
              />
            </div>
            
            <div>
              <Label className="text-slate-700 dark:text-slate-300">Priority</Label>
              <div className="flex gap-2 mt-2">
                <Button
                  type="button"
                  variant={newStop.priority === 'first' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setNewStop({ ...newStop, priority: 'first' })}
                  className={newStop.priority === 'first' ? 'bg-red-500 hover:bg-red-600' : 'border-slate-300 dark:border-slate-600'}
                >
                  <Flag className="h-3 w-3 mr-1" />
                  FIRST
                </Button>
                <Button
                  type="button"
                  variant={newStop.priority === 'normal' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setNewStop({ ...newStop, priority: 'normal' })}
                  className={newStop.priority === 'normal' ? 'bg-slate-600' : 'border-slate-300 dark:border-slate-600'}
                >
                  Normal
                </Button>
                <Button
                  type="button"
                  variant={newStop.priority === 'last' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setNewStop({ ...newStop, priority: 'last' })}
                  className={newStop.priority === 'last' ? 'bg-blue-500 hover:bg-blue-600' : 'border-slate-300 dark:border-slate-600'}
                >
                  <Flag className="h-3 w-3 mr-1" />
                  LAST
                </Button>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddStopDialog(false)} className="border-slate-300 dark:border-slate-600">
              Cancel
            </Button>
            <Button onClick={handleAddStop} className="btn-accent" disabled={geocoding}>
              {geocoding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Add Stop
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Stop Dialog */}
      <Dialog open={showEditStopDialog} onOpenChange={(open) => {
        setShowEditStopDialog(open);
        if (!open) {
          setEditingStop(null);
          setEditAddressInput('');
          setEditAddressSuggestions([]);
          setShowEditSuggestions(false);
        } else if (editingStop) {
          setEditAddressInput(editingStop.address || '');
        }
      }}>
        <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 z-[100]">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white">Edit Stop</DialogTitle>
          </DialogHeader>
          
          {editingStop && (
            <div className="space-y-4 py-4">
              <div className="relative">
                <Label className="text-slate-700 dark:text-slate-300">Address *</Label>
                <Input
                  placeholder="Start typing address..."
                  value={editAddressInput}
                  onChange={(e) => handleEditAddressInputChange(e.target.value)}
                  onFocus={() => editAddressSuggestions.length > 0 && setShowEditSuggestions(true)}
                  className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 mt-1"
                  autoComplete="off"
                />
                {/* Address suggestions dropdown */}
                {showEditSuggestions && editAddressSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {editAddressSuggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 border-b border-slate-100 dark:border-slate-700 last:border-0"
                        onClick={() => handleSelectEditSuggestion(suggestion)}
                      >
                        {suggestion.address}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700 dark:text-slate-300">Defendant Name</Label>
                  <Input
                    placeholder="e.g., John Smith"
                    value={editingStop.defendant_name || ''}
                    onChange={(e) => setEditingStop({ ...editingStop, defendant_name: e.target.value })}
                    className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 dark:text-slate-300">Case Number</Label>
                  <Input
                    placeholder="e.g., CV-2026-1234"
                    value={editingStop.case_number || ''}
                    onChange={(e) => setEditingStop({ ...editingStop, case_number: e.target.value })}
                    className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 mt-1"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-slate-700 dark:text-slate-300">Notes</Label>
                <Textarea
                  placeholder="e.g., Gate code: 1234, Ring doorbell twice"
                  value={editingStop.notes || ''}
                  onChange={(e) => setEditingStop({ ...editingStop, notes: e.target.value })}
                  className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 mt-1"
                  rows={2}
                />
              </div>
              
              <div>
                <Label className="text-slate-700 dark:text-slate-300 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Service Time (optional)
                </Label>
                <p className="text-xs text-slate-500 mb-2">Override default time at this stop (e.g., 30 min for notary)</p>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="1"
                    max="240"
                    placeholder={`Default: ${selectedRoute?.time_per_stop_minutes || 5}`}
                    value={editingStop.service_time_minutes ?? ''}
                    onChange={(e) => setEditingStop({ 
                      ...editingStop, 
                      service_time_minutes: e.target.value === '' ? null : parseInt(e.target.value) || null
                    })}
                    className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 w-24"
                  />
                  <span className="text-sm text-slate-500">minutes</span>
                  {editingStop.service_time_minutes != null && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-slate-500 hover:text-slate-700 dark:text-slate-300 h-6 px-2"
                      onClick={() => setEditingStop({ ...editingStop, service_time_minutes: null })}
                    >
                      Reset to default
                    </Button>
                  )}
                </div>
              </div>
              
              <div>
                <Label className="text-slate-700 dark:text-slate-300">Priority</Label>
                <div className="flex gap-2 mt-2">
                  <Button
                    type="button"
                    variant={editingStop.priority === 'first' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setEditingStop({ ...editingStop, priority: 'first' })}
                    className={editingStop.priority === 'first' ? 'bg-red-500 hover:bg-red-600' : 'border-slate-300 dark:border-slate-600'}
                  >
                    <Flag className="h-3 w-3 mr-1" />
                    FIRST
                  </Button>
                  <Button
                    type="button"
                    variant={editingStop.priority === 'normal' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setEditingStop({ ...editingStop, priority: 'normal' })}
                    className={editingStop.priority === 'normal' ? 'bg-slate-600' : 'border-slate-300 dark:border-slate-600'}
                  >
                    Normal
                  </Button>
                  <Button
                    type="button"
                    variant={editingStop.priority === 'last' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setEditingStop({ ...editingStop, priority: 'last' })}
                    className={editingStop.priority === 'last' ? 'bg-blue-500 hover:bg-blue-600' : 'border-slate-300 dark:border-slate-600'}
                  >
                    <Flag className="h-3 w-3 mr-1" />
                    LAST
                  </Button>
                </div>
              </div>
              
              <div>
                <Label className="text-slate-700 dark:text-slate-300 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Time Constraint (optional)
                </Label>
                <p className="text-xs text-slate-500 mb-2">Set when you need to arrive at this stop</p>
                
                {/* Constraint type selector */}
                <div className="flex flex-wrap gap-2 mb-3">
                  <Button
                    type="button"
                    variant={editingStop.time_constraint_type === 'arrive_before' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setEditingStop({ ...editingStop, time_constraint_type: 'arrive_before', time_window_start: '', time_window_end: '' })}
                    className={editingStop.time_constraint_type === 'arrive_before' ? 'bg-amber-600 hover:bg-amber-700' : 'border-slate-300 dark:border-slate-600'}
                  >
                    Arrive Before
                  </Button>
                  <Button
                    type="button"
                    variant={editingStop.time_constraint_type === 'arrive_between' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setEditingStop({ ...editingStop, time_constraint_type: 'arrive_between', time_constraint_time: '' })}
                    className={editingStop.time_constraint_type === 'arrive_between' ? 'bg-teal-600 hover:bg-teal-700' : 'border-slate-300 dark:border-slate-600'}
                  >
                    Arrive Between
                  </Button>
                  <Button
                    type="button"
                    variant={editingStop.time_constraint_type === 'arrive_after' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setEditingStop({ ...editingStop, time_constraint_type: 'arrive_after', time_window_start: '', time_window_end: '' })}
                    className={editingStop.time_constraint_type === 'arrive_after' ? 'bg-blue-600 hover:bg-blue-700' : 'border-slate-300 dark:border-slate-600'}
                  >
                    Arrive After
                  </Button>
                </div>
                
                {/* Time input(s) based on constraint type */}
                {editingStop.time_constraint_type === 'arrive_before' && (
                  <div className="flex items-center gap-2">
                    <Label className="text-slate-400 text-xs whitespace-nowrap">Must arrive before</Label>
                    <Input
                      type="time"
                      value={editingStop.time_constraint_time || ''}
                      onChange={(e) => setEditingStop({ ...editingStop, time_constraint_time: e.target.value })}
                      className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 w-36"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-slate-500 hover:text-slate-700 dark:text-slate-300 h-6 px-2"
                      onClick={() => setEditingStop({ ...editingStop, time_constraint_type: 'none', time_constraint_time: '' })}
                    >
                      Clear
                    </Button>
                  </div>
                )}
                
                {editingStop.time_constraint_type === 'arrive_between' && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-400 text-xs">Window start</Label>
                        <Input
                          type="time"
                          value={editingStop.time_window_start || ''}
                          onChange={(e) => setEditingStop({ ...editingStop, time_window_start: e.target.value })}
                          className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-400 text-xs">Window end</Label>
                        <Input
                          type="time"
                          value={editingStop.time_window_end || ''}
                          onChange={(e) => setEditingStop({ ...editingStop, time_window_end: e.target.value })}
                          className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 mt-1"
                        />
                      </div>
                    </div>
                    <p className="text-[10px] text-slate-500">
                      For overnight windows (e.g., 11:55 PM to 12:30 AM), the system automatically detects the next day.
                    </p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-slate-500 hover:text-slate-700 dark:text-slate-300 h-6 px-2"
                      onClick={() => setEditingStop({ ...editingStop, time_constraint_type: 'none', time_window_start: '', time_window_end: '' })}
                    >
                      Clear constraint
                    </Button>
                  </div>
                )}
                
                {editingStop.time_constraint_type === 'arrive_after' && (
                  <div className="flex items-center gap-2">
                    <Label className="text-slate-400 text-xs whitespace-nowrap">Don't arrive before</Label>
                    <Input
                      type="time"
                      value={editingStop.time_constraint_time || ''}
                      onChange={(e) => setEditingStop({ ...editingStop, time_constraint_time: e.target.value })}
                      className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 w-36"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-slate-500 hover:text-slate-700 dark:text-slate-300 h-6 px-2"
                      onClick={() => setEditingStop({ ...editingStop, time_constraint_type: 'none', time_constraint_time: '' })}
                    >
                      Clear
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditStopDialog(false)} className="border-slate-300 dark:border-slate-600">
              Cancel
            </Button>
            <Button onClick={handleSaveEditedStop} className="btn-accent">
              <Check className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Route Dialog */}
      <Dialog open={showEditRouteDialog} onOpenChange={(open) => {
        setShowEditRouteDialog(open);
        if (!open) setEditingRoute(null);
      }}>
        <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 z-[100]">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white">Edit Route Settings</DialogTitle>
          </DialogHeader>
          
          {editingRoute && (
            <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
              <div>
                <Label className="text-slate-700 dark:text-slate-300">Route Name</Label>
                <Input
                  placeholder="e.g., Tuesday Downtown Route"
                  value={editingRoute.name}
                  onChange={(e) => setEditingRoute({ ...editingRoute, name: e.target.value })}
                  className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 mt-1"
                />
              </div>
              
              <div>
                <Label className="text-slate-700 dark:text-slate-300">Date</Label>
                <Input
                  type="date"
                  value={editingRoute.date}
                  onChange={(e) => setEditingRoute({ ...editingRoute, date: e.target.value })}
                  className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 mt-1"
                />
              </div>
              
              <div>
                <Label className="text-slate-700 dark:text-slate-300">Starting Point</Label>
                <div className="space-y-2 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={editingRoute.use_current_location}
                      onChange={() => {
                        setEditingRoute({ ...editingRoute, use_current_location: true });
                        setStartAddressVerified(false);
                        setShowStartSuggestions(false);
                      }}
                      className="text-teal-500"
                    />
                    <LocateFixed className="h-4 w-4 text-teal-500" />
                    <span className="text-slate-700 dark:text-slate-300">Use my current location</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={!editingRoute.use_current_location}
                      onChange={() => setEditingRoute({ ...editingRoute, use_current_location: false })}
                      className="text-teal-500"
                    />
                    <MapPin className="h-4 w-4 text-slate-400" />
                    <span className="text-slate-700 dark:text-slate-300">Enter starting address</span>
                  </label>
                  {!editingRoute.use_current_location && (
                    <div className="relative">
                      <div className="relative">
                        <Input
                          placeholder="Start typing an address..."
                          value={editingRoute.start_address}
                          onChange={(e) => handleStartAddressChange(e.target.value, false)}
                          onFocus={() => editingRoute.start_address.length >= 3 && setShowStartSuggestions(true)}
                          className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 mt-2"
                        />
                        {searchingStartAddress && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                          </div>
                        )}
                      </div>
                      
                      {showStartSuggestions && startAddressSuggestions.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {startAddressSuggestions.map((suggestion, index) => (
                            <button
                              key={index}
                              type="button"
                              className="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-start gap-2 border-b border-slate-200 dark:border-slate-700 last:border-0"
                              onClick={() => handleSelectStartSuggestion(suggestion, false)}
                            >
                              <MapPin className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" />
                              <span>{suggestion.address}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      
                      {(startAddressVerified || editingRoute.start_address) && editingRoute.start_address && (
                        <p className="text-xs text-teal-500 mt-1 flex items-center gap-1">
                          <Check className="h-3 w-3" /> Address verified
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-slate-700 dark:text-slate-300">Return to Start Point</Label>
                  <Switch
                    checked={editingRoute.return_to_start}
                    onCheckedChange={(checked) => {
                      setEditingRoute({ ...editingRoute, return_to_start: checked, end_address: '' });
                      setEndAddressVerified(false);
                      setShowEndSuggestions(false);
                    }}
                  />
                </div>
                <p className="text-xs text-slate-500">When enabled, your route will end where it started</p>
              </div>
              
              {!editingRoute.return_to_start && (
                <div className="relative">
                  <Label className="text-slate-700 dark:text-slate-300">Ending Address (Optional)</Label>
                  <div className="relative mt-1">
                    <Input
                      placeholder="Start typing an address..."
                      value={editingRoute.end_address}
                      onChange={(e) => handleEndAddressChange(e.target.value, false)}
                      onFocus={() => editingRoute.end_address.length >= 3 && setShowEndSuggestions(true)}
                      className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600"
                    />
                    {searchingEndAddress && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                      </div>
                    )}
                  </div>
                  
                  {showEndSuggestions && endAddressSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {endAddressSuggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-start gap-2 border-b border-slate-200 dark:border-slate-700 last:border-0"
                          onClick={() => handleSelectEndSuggestion(suggestion, false)}
                        >
                          <MapPin className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" />
                          <span>{suggestion.address}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {(endAddressVerified || editingRoute.end_address) && editingRoute.end_address && (
                    <p className="text-xs text-teal-500 mt-1 flex items-center gap-1">
                      <Check className="h-3 w-3" /> Address verified
                    </p>
                  )}
                  <p className="text-xs text-slate-500 mt-1">Leave blank to end at your last stop</p>
                </div>
              )}
              
              <div>
                <Label className="text-slate-700 dark:text-slate-300">Time Per Stop (minutes)</Label>
                <div className="flex items-center gap-3 mt-2">
                  <Input
                    type="number"
                    min="0"
                    max="60"
                    value={editingRoute.time_per_stop_minutes}
                    onChange={(e) => setEditingRoute({ ...editingRoute, time_per_stop_minutes: parseInt(e.target.value) || 0 })}
                    className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 w-24"
                  />
                  <span className="text-sm text-slate-400">minutes at each stop</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">This time is added to the total route duration for service at each stop</p>
              </div>

              {/* Routing preferences */}
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40 p-3 space-y-2">
                <Label className="text-slate-700 dark:text-slate-300 text-sm">Routing preferences</Label>
                <p className="text-xs text-slate-500 -mt-1">Best-effort — if no alternative exists, the road is used anyway.</p>
                <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700 dark:text-slate-300">
                  <input type="checkbox" checked={!!editingRoute.avoid_tolls}
                         onChange={(e) => setEditingRoute({ ...editingRoute, avoid_tolls: e.target.checked })}
                         data-testid="edit-route-avoid-tolls"
                         className="h-4 w-4 accent-[hsl(226,100%,55%)]" />
                  Avoid toll roads
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700 dark:text-slate-300">
                  <input type="checkbox" checked={!!editingRoute.avoid_highways}
                         onChange={(e) => setEditingRoute({ ...editingRoute, avoid_highways: e.target.checked })}
                         data-testid="edit-route-avoid-highways"
                         className="h-4 w-4 accent-[hsl(226,100%,55%)]" />
                  Avoid highways / motorways
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700 dark:text-slate-300">
                  <input type="checkbox" checked={!!editingRoute.avoid_unpaved}
                         onChange={(e) => setEditingRoute({ ...editingRoute, avoid_unpaved: e.target.checked })}
                         data-testid="edit-route-avoid-unpaved"
                         className="h-4 w-4 accent-[hsl(226,100%,55%)]" />
                  Avoid unpaved / dirt roads
                </label>
              </div>
              
              <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                {(() => {
                  const scheduledTimePassed = editingRoute.scheduled_start_time && !isScheduledTimeInFuture(editingRoute.scheduled_start_time);
                  return (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Clock className={`h-4 w-4 ${scheduledTimePassed ? 'text-slate-400' : 'text-teal-500'}`} />
                          <Label className={scheduledTimePassed ? 'text-slate-400' : 'text-slate-700 dark:text-slate-300'}>Scheduled Start Time</Label>
                        </div>
                        <Switch
                          checked={!!editingRoute.scheduled_start_time && !scheduledTimePassed}
                          disabled={scheduledTimePassed}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              const now = new Date();
                              now.setHours(now.getHours() + 1);
                              now.setMinutes(Math.round(now.getMinutes() / 15) * 15);
                              const defaultTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
                              setEditingRoute({ ...editingRoute, scheduled_start_time: defaultTime });
                            } else {
                              setEditingRoute({ ...editingRoute, scheduled_start_time: null });
                            }
                          }}
                        />
                      </div>
                      {scheduledTimePassed ? (
                        <p className="text-xs text-amber-500 mb-2">Scheduled time has passed. Clear and re-optimize to use current time.</p>
                      ) : (
                        <p className="text-xs text-slate-500 mb-2">Pre-plan when you'll start this route. ETAs will calculate from this time.</p>
                      )}
                      
                      {editingRoute.scheduled_start_time && !scheduledTimePassed && (
                        <div className="flex items-center gap-3 mt-2">
                          <Input
                            type="time"
                            value={editingRoute.scheduled_start_time}
                            onChange={(e) => setEditingRoute({ ...editingRoute, scheduled_start_time: e.target.value })}
                            className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 w-32"
                          />
                          <span className="text-sm text-slate-400">departure time</span>
                        </div>
                      )}
                      {editingRoute.scheduled_start_time && !scheduledTimePassed && (
                        <p className="text-xs text-teal-500 mt-2 flex items-center gap-1">
                          <Check className="h-3 w-3" /> 
                          Optimizing as if leaving at {formatTime12h(editingRoute.scheduled_start_time)}. Auto-clears when route starts.
                        </p>
                      )}
                      {scheduledTimePassed && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2 text-xs"
                          onClick={() => setEditingRoute({ ...editingRoute, scheduled_start_time: null })}
                        >
                          Clear Scheduled Time
                        </Button>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditRouteDialog(false)} className="border-slate-300 dark:border-slate-600">
              Cancel
            </Button>
            <Button onClick={handleSaveEditedRoute} className="btn-accent" disabled={editingRouteGeocoding}>
              {editingRouteGeocoding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Navigation App Picker Dialog */}
      <Dialog open={showNavPicker} onOpenChange={setShowNavPicker}>
        <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white">Choose Navigation App</DialogTitle>
            <DialogDescription className="text-slate-400">
              Select your preferred navigation app. This will be your default.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-4">
            <Button 
              variant="outline" 
              className="h-14 justify-start gap-3 border-slate-200 dark:border-slate-700 hover:bg-slate-800"
              onClick={() => handleNavAppSelect('google')}
            >
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Map className="h-5 w-5 text-blue-400" />
              </div>
              <div className="text-left">
                <p className="text-slate-900 dark:text-white font-medium">Google Maps</p>
                <p className="text-slate-500 text-xs">Best for Android users</p>
              </div>
            </Button>
            <Button 
              variant="outline" 
              className="h-14 justify-start gap-3 border-slate-200 dark:border-slate-700 hover:bg-slate-800"
              onClick={() => handleNavAppSelect('apple')}
            >
              <div className="w-10 h-10 rounded-lg bg-slate-500/20 flex items-center justify-center">
                <Map className="h-5 w-5 text-slate-400" />
              </div>
              <div className="text-left">
                <p className="text-slate-900 dark:text-white font-medium">Apple Maps</p>
                <p className="text-slate-500 text-xs">Best for iPhone users</p>
              </div>
            </Button>
            <Button 
              variant="outline" 
              className="h-14 justify-start gap-3 border-slate-200 dark:border-slate-700 hover:bg-slate-800"
              onClick={() => handleNavAppSelect('waze')}
            >
              <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                <Map className="h-5 w-5 text-cyan-400" />
              </div>
              <div className="text-left">
                <p className="text-slate-900 dark:text-white font-medium">Waze</p>
                <p className="text-slate-500 text-xs">Best for real-time traffic</p>
              </div>
            </Button>
          </div>
          <p className="text-xs text-slate-500 text-center">
            To change later, tap the app name next to the Navigate button
          </p>
        </DialogContent>
      </Dialog>

      {/* Import CSV/Excel Dialog */}
      <Dialog open={showImportDialog} onOpenChange={(open) => {
        setShowImportDialog(open);
        if (!open) {
          setImportFile(null);
          setImportPreview(null);
        }
      }}>
        <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 z-[100] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white">Import Stops from File</DialogTitle>
            <DialogDescription className="text-slate-500">
              Upload a CSV or Excel file with addresses to bulk add stops to your route
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Drop Zone */}
            {!importPreview && (
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleFileDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  dragOver 
                    ? 'border-teal-500 bg-teal-500/10' 
                    : 'border-slate-300 dark:border-slate-600 hover:border-teal-500/50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => e.target.files[0] && handleFileSelect(e.target.files[0])}
                  className="hidden"
                />
                {importing ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
                    <p className="text-slate-400">Processing file...</p>
                  </div>
                ) : (
                  <>
                    <svg className="h-12 w-12 mx-auto text-slate-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p className="text-slate-700 dark:text-slate-300 font-medium">
                      Drag & drop your file here
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                      or click to browse
                    </p>
                    <p className="text-xs text-slate-400 mt-3">
                      Supports CSV, Excel (.xlsx, .xls) — no headers needed!
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Download Template Link */}
            {!importPreview && (
              <div className="text-center space-y-1">
                <button
                  onClick={handleDownloadTemplate}
                  className="text-sm text-teal-400 hover:text-teal-300 underline"
                >
                  Download example file
                </button>
                <p className="text-xs text-slate-500">
                  Column order: Address, Defendant, Case #, Notes, Priority, Time Constraint, Start, End
                </p>
              </div>
            )}

            {/* Preview Results */}
            {importPreview && (
              <div className="space-y-4">
                {/* Summary */}
                <div className="flex gap-4 p-3 bg-slate-100 dark:bg-slate-900 rounded-lg">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-teal-500">{importPreview.successful}</p>
                    <p className="text-xs text-slate-500">Addresses Found</p>
                  </div>
                  {importPreview.failed > 0 && (
                    <div className="text-center">
                      <p className="text-2xl font-bold text-red-400">{importPreview.failed}</p>
                      <p className="text-xs text-slate-500">Failed</p>
                    </div>
                  )}
                </div>

                {/* Stops Preview */}
                {importPreview.stops.length > 0 && (
                  <div className="max-h-60 overflow-y-auto space-y-2">
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Preview:</p>
                    {importPreview.stops.slice(0, 10).map((stop, idx) => (
                      <div key={idx} className="flex items-start gap-2 p-2 bg-slate-50 dark:bg-slate-900/50 rounded text-sm">
                        <span className="bg-teal-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          {idx + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-slate-700 dark:text-slate-300 truncate">{stop.address}</p>
                          {(stop.defendant_name || stop.case_number) && (
                            <p className="text-xs text-slate-500 truncate">
                              {stop.defendant_name}{stop.defendant_name && stop.case_number && ' • '}{stop.case_number}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                    {importPreview.stops.length > 10 && (
                      <p className="text-xs text-slate-500 text-center">
                        ...and {importPreview.stops.length - 10} more
                      </p>
                    )}
                  </div>
                )}

                {/* Errors */}
                {importPreview.errors.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-red-400 mb-2">Errors:</p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {importPreview.errors.map((err, idx) => (
                        <p key={idx} className="text-xs text-red-400 bg-red-500/10 p-2 rounded">
                          Row {err.row}: {err.error} {err.address && `(${err.address})`}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            {importPreview ? (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setImportFile(null);
                    setImportPreview(null);
                  }}
                  className="border-slate-300 dark:border-slate-600"
                >
                  Upload Different File
                </Button>
                <Button 
                  onClick={handleConfirmImport} 
                  className="btn-accent"
                  disabled={importPreview.stops.length === 0}
                >
                  Add {importPreview.stops.length} Stops
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setShowImportDialog(false)} className="border-slate-300 dark:border-slate-600">
                Cancel
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Route Analytics Dialog */}
      <RouteAnalyticsDialog
        open={showAnalyticsDialog}
        onOpenChange={setShowAnalyticsDialog}
        routeId={analyticsRouteId}
      />

      {/* Add Stop Mid-Route Dialog */}
      <AddStopMidRouteDialog
        open={showAddStopMidRouteDialog}
        onOpenChange={setShowAddStopMidRouteDialog}
        routeId={selectedRoute?.id}
        existingStops={selectedRoute?.stops || []}
        onStopAdded={(result) => {
          // Refresh route data
          if (result.route) {
            setSelectedRoute(result.route);
          }
          fetchRoutes();
          toast.success('Stop added! Remember to re-optimize the route.');
        }}
      />

      {/* Route PDF dialog */}
      <Dialog open={showPdfDialog} onOpenChange={setShowPdfDialog}>
        <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white flex items-center gap-2">
              <FileText className="h-4 w-4 text-[hsl(226,100%,55%)]" />
              Download Route as PDF
            </DialogTitle>
          </DialogHeader>

          <div className="mt-2 space-y-3">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              A clean, printable sheet with the route summary, all stops, and your branding.
            </p>

            <label className="flex items-start gap-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-[hsl(226,100%,55%)] p-3 cursor-pointer transition-colors">
              <input
                type="checkbox"
                checked={pdfIncludeDirections}
                onChange={(e) => setPdfIncludeDirections(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[hsl(226,100%,55%)]"
                data-testid="pdf-include-directions"
              />
              <div>
                <div className="text-sm font-medium text-slate-900 dark:text-white">Include turn-by-turn worded directions</div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  e.g. <span className="font-mono italic">"Turn right onto Main St"</span> — each leg, start → stop → stop → end.
                </div>
                <div className="text-[11px] text-amber-600 dark:text-amber-400 mt-1.5">
                  Adds ~1 Mapbox call per leg. Slightly slower to generate.
                </div>
              </div>
            </label>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowPdfDialog(false)} className="border-slate-300 dark:border-slate-600">
              Cancel
            </Button>
            <Button
              onClick={() => handleDownloadRoutePdf(pdfIncludeDirections)}
              disabled={generatingPdf}
              className="btn-accent"
              data-testid="generate-route-pdf"
            >
              {generatingPdf ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
              {generatingPdf ? 'Generating…' : 'Download PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share / Tracking link dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white flex items-center gap-2">
              <Share2 className="h-4 w-4 text-[hsl(226,100%,55%)]" />
              Client tracking link
            </DialogTitle>
          </DialogHeader>

          {selectedRoute?.share_token && (
            <>
              {/* The link */}
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-3">
                <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 dark:text-slate-400 mb-1">PUBLIC LINK</div>
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={`${window.location.origin}/track/${selectedRoute.share_token}`}
                    className="flex-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md px-2 py-1.5 text-xs font-mono text-slate-700 dark:text-slate-300 truncate"
                    onClick={(e) => e.target.select()}
                    data-testid="share-link-input"
                  />
                  <Button size="sm" onClick={copyShareLink} className="btn-accent" data-testid="copy-share-link">
                    <Copy className="h-3.5 w-3.5 mr-1" />
                    Copy
                  </Button>
                </div>
              </div>

              {/* Visibility toggles */}
              <div className="mt-4 rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2.5">
                <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 dark:text-slate-400">VISIBLE TO CLIENT</div>
                {[
                  { key: 'share_show_map',          label: 'Live map',                 sub: 'Map + route polyline + stop pins' },
                  { key: 'share_show_progress',     label: 'Stop progress',            sub: 'e.g., "3 of 5 stops complete"' },
                  { key: 'share_show_eta',          label: 'ETA & remaining time',     sub: 'Estimated arrival & finish time' },
                  { key: 'share_show_addresses',    label: 'Stop addresses',           sub: 'Off = "Address withheld by server"' },
                  { key: 'share_show_other_stops',  label: 'Show all stops',           sub: 'Off = only their stop is shown' },
                  { key: 'share_show_driver_name',  label: 'Your name / company',      sub: 'Off = "Your server" (anonymous)' },
                ].map(opt => {
                  const val = selectedRoute[opt.key] ?? true;
                  return (
                    <label key={opt.key} className="flex items-start gap-3 cursor-pointer rounded-md hover:bg-slate-50 dark:hover:bg-slate-900/40 p-2 -m-2 transition-colors">
                      <input
                        type="checkbox"
                        checked={val}
                        onChange={(e) => updateShareVisibility(opt.key, e.target.checked)}
                        data-testid={`share-vis-${opt.key}`}
                        className="mt-0.5 h-4 w-4 accent-[hsl(226,100%,55%)]"
                      />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900 dark:text-white">{opt.label}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">{opt.sub}</div>
                      </div>
                    </label>
                  );
                })}
              </div>

              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  onClick={async () => {
                    if (!window.confirm('Revoke this link? Anyone with the link will lose access.')) return;
                    try {
                      await api.revokeShareLink(selectedRoute.id);
                      setSelectedRoute({ ...selectedRoute, share_token: null });
                      setShowShareDialog(false);
                      toast.success('Share link revoked');
                    } catch {
                      toast.error('Failed to revoke link');
                    }
                  }}
                  className="border-slate-300 dark:border-slate-600 text-red-600 dark:text-red-400 hover:text-red-700"
                  data-testid="revoke-share-link"
                >
                  Revoke link
                </Button>
                <Button onClick={() => setShowShareDialog(false)} className="btn-accent">Done</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Template Picker */}
      <Dialog open={showTemplatePicker} onOpenChange={setShowTemplatePicker}>
        <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white flex items-center gap-2">
              <Star className="h-4 w-4 text-amber-400" />
              Create from template
            </DialogTitle>
          </DialogHeader>
          <div className="mt-3 space-y-2 max-h-[420px] overflow-y-auto">
            {templatesLoading ? (
              <div className="py-12 text-center text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading templates…</div>
            ) : templateRoutes.length === 0 ? (
              <div className="py-10 text-center">
                <Star className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-600 dark:text-slate-300 font-medium">No templates yet</p>
                <p className="text-xs text-slate-500 mt-1">Open any route → ⋮ menu → &ldquo;Save as template&rdquo; to make one.</p>
              </div>
            ) : templateRoutes.map(t => (
              <button
                key={t.id}
                onClick={() => handleCreateFromTemplate(t.id)}
                data-testid={`template-${t.id}`}
                className="w-full text-left rounded-lg border border-slate-200 dark:border-slate-700 hover:border-[hsl(226,100%,55%)] dark:hover:border-[hsl(226,100%,55%)] hover:bg-slate-50 dark:hover:bg-slate-900/40 p-3 transition-colors"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-slate-900 dark:text-white truncate">{t.template_name || t.name}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{(t.stops || []).length} stop{(t.stops || []).length === 1 ? '' : 's'}</div>
                  </div>
                  <ArrowLeftRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
                </div>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplatePicker(false)} className="border-slate-300 dark:border-slate-600">
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
